import { jsx, jsxs, Fragment as Fragment$1 } from "react/jsx-runtime";
import { Routes, Route } from "react-router";
import { App, useUser, useConfig, RequirePolicy, createEmbed } from "@livefree/applications";
import { ReactiveCore, replaceRows, DexieDatabaseProvider, useSuccessReactor, useErrorReactor } from "@livefree/reactive";
import { MqlRequestBuilder, RequestMetadata, dexieRequestBrokerAddon, Sorting, RequestBrokerDataGrid, RequestBroker } from "@livefree/request-broker";
import { RestClient } from "@livefree/js-sdk";
import { EnvironmentConfigurations, useComponent, useRuntimeProvider } from "@livefree/runtime";
import { formatCurrencyUSD, toISO, DateTime, parseAndFormatDate, parseAndFormatDateTime, parseAndFormatTime } from "@livefree/common";
import { useState, Fragment, useRef, useEffect, useCallback, useMemo, createElement } from "react";
import { ListBox, Label, Button, Skeleton, Table, Card, TextField as TextField$1, TextArea, Alert, Tabs, ButtonGroup, Breadcrumbs, BreadcrumbsItem, Tooltip, Dropdown, CardContent, useOverlayState, Modal } from "@heroui/react";
import { SelectField, Uniformly, TextField, masks, DateField, TextAreaField, SwitchField, ToggleButtonsField, TabsField, DateTimeField } from "@livefree/uniformly";
import { DrawerActions, SaveButton, withDrawerController, LoadingBar, ConfirmableButton, FieldRow, Pill, DeleteButton, useDrawerController, Forbidden, FlatCard } from "@livefree/react-ui";
import { EmptyState, Widget } from "@heroui-pro/react";
import { KPI } from "@heroui-pro/react/kpi";
import { KPIGroup } from "@heroui-pro/react/kpi-group";
import { Filters, FilterInput, FilterSelect, FilterDateRange } from "@livefree/filters";
const reactive = new ReactiveCore("billing");
const PaymentType = {
  CreditCard: 1,
  ACH: 2,
  Refund: 3,
  Check: 5,
  Cash: 6,
  MoneyOrder: 7
};
const PaymentStatusType = {
  Pending: 1,
  Authorized: 2,
  Paid: 3,
  PartiallyRefunded: 4,
  Refunded: 5,
  Voided: 6,
  Declined: 7,
  PartiallyPaid: 8,
  Error: 9,
  InvalidRouting: 10,
  InvalidAccount: 11,
  AccountNotFound: 12,
  NSF: 13,
  AccountClosed: 14,
  CustomerRejected: 15,
  AccountFrozen: 16
};
const CheckPayerType = {
  Subscriber: "Subscriber",
  BillPayer: "Bill Payer",
  ThirdParty: "Third-Party"
};
const AllocationSourceType = {
  Credit: 2
};
const CreditReasonType = {
  CollectionsAmount: 14,
  CollectionsFee: 15,
  DeviceRecoveryAmount: 16,
  DeviceRecoveryFee: 17,
  WriteOff: 18
};
const CreditStatusType = {
  Pending: 1,
  Approved: 2,
  Declined: 3
};
const DisputeOutcome = {
  /** The money came back: the payment is Paid again and applied to the open orders. */
  Won: 1,
  /** The money is gone: the payment stays disputed; the customer can be charged again, plus a fee. */
  Lost: 2
};
class PaymentsClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "billing/payments");
  }
  /** `GET /billing/payments?accountId=` — every payment on an account. There is no list-all. */
  async getByAccount(accountId) {
    return await this.get({ accountId: String(accountId) });
  }
  /** `GET /billing/payments/{id}` — rejects as not-found on a missing id (never 200-with-null). */
  async getById(id) {
    return await this.fetch(id);
  }
  /**
   * `POST /billing/payments` — record a payment. A payment that is already due is queued for the
   * processor as part of creation, so do not follow this with {@link queue}.
   */
  async submit(payment) {
    return await this.create(payment);
  }
  /** `PUT /billing/payments/{id}` */
  async save(id, payment) {
    return await this.update(id, payment);
  }
  /** `DELETE /billing/payments/{id}` — 204 No Content. */
  async remove(id) {
    await this.delete(id);
  }
  /**
   * `POST /billing/payments/{id}/queue` — re-queue a stored payment for the processor (one whose
   * date has since arrived, or whose first attempt needs repeating). Sends no body: the API uses the
   * amount and type on record.
   */
  async queue(id) {
    return await this.httpPOST({ url: this.appendUrl(`/${id}/queue`) });
  }
  /**
   * `POST /billing/payments/refund` — refund all or part of a payment. Returns the new refund
   * payment (type Refund, parented to the original).
   */
  async refund(request) {
    return await this.httpPOST({
      url: this.appendUrl("/refund"),
      body: request
    });
  }
  /**
   * `POST /billing/payments/{id}/dispute` — mark a Paid card or ACH payment as disputed. It is taken
   * off the orders it paid, and the account's active subscription is put on hold.
   */
  async dispute(id, request) {
    return await this.httpPOST({
      url: this.appendUrl(`/${id}/dispute`),
      body: request
    });
  }
  /** `POST /billing/payments/{id}/dispute/resolve` — end an open dispute as Won or Lost. */
  async resolveDispute(id, request) {
    return await this.httpPOST({
      url: this.appendUrl(`/${id}/dispute/resolve`),
      body: request
    });
  }
  /** `GET /billing/payments/{id}/status-history` — disputes, their resolutions and refunds that ended one, newest first. */
  async getStatusHistory(id) {
    return await this.httpGET({
      url: this.appendUrl(`/${id}/status-history`)
    });
  }
  /** `GET /billing/payments/{id}/allocations` — the orders this payment has been applied to. */
  async getAllocations(id) {
    return await this.httpGET({ url: this.appendUrl(`/${id}/allocations`) });
  }
}
class CreditsClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "billing/credits");
  }
  /** `GET /billing/credits?accountId=` — every credit on an account, unpaged. */
  async getByAccount(accountId) {
    return await this.get({ accountId: String(accountId) });
  }
  /**
   * `POST /billing/credits/query` — a page of credits across every account the user can see
   * (`values` plus `totalCount`/`skip`/`take`). The one read that is not scoped to an account.
   *
   * Pass the request-broker `metadata` (the filters/sorting/paging the list collected); it is
   * compiled to MQL with {@link MqlRequestBuilder}. With no metadata, the first page of everything.
   * Not named `query`, which `BaseEndpoint` already takes.
   *
   * Sent as `text/plain`: the route binds a `PlainTextBody`, and the SDK only JSON-encodes when the
   * content type is `application/json` — the default would post a quoted string the API cannot bind.
   */
  async queryCredits(metadata) {
    const query = metadata ? MqlRequestBuilder.create(metadata).build("CreditModel") : "FETCH CreditModel;";
    return await this.httpPOST({
      url: this.appendUrl("/query"),
      body: query,
      options: { contentType: "text/plain" }
    });
  }
  /** `GET /billing/credits/reasons` — the reasons a credit can be granted for (a lookup list). */
  async getReasons() {
    return await this.httpGET({ url: this.appendUrl("/reasons") });
  }
  /** `GET /billing/credits/{id}` — rejects as not-found on a missing id (never 200-with-null). */
  async getById(id) {
    return await this.fetch(id);
  }
  /** `POST /billing/credits` — grant a credit. It starts Pending; see {@link updateStatus}. */
  async submit(credit) {
    return await this.create(credit);
  }
  /**
   * `POST /billing/credits/bulk` — several credits for one account in one transaction, each created
   * approved. A 400 lists every problem in the batch; nothing is written unless all are accepted.
   */
  async submitMany(request) {
    return await this.httpPOST({ url: this.appendUrl("/bulk"), body: request });
  }
  /** `PUT /billing/credits/{id}/status` — approve or decline a credit (adds a status history row). */
  async updateStatus(id, request) {
    return await this.httpPUT({
      url: this.appendUrl(`/${id}/status`),
      body: request
    });
  }
  /** `GET /billing/credits/{id}/allocations` — the orders this credit has been applied to. */
  async getAllocations(id) {
    return await this.httpGET({ url: this.appendUrl(`/${id}/allocations`) });
  }
}
class AllocationsClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "billing/allocations");
  }
  /** `GET /billing/allocations?orderId=` — the payments and credits applied to one order. */
  async getByOrder(orderId) {
    return await this.get({ orderId: String(orderId) });
  }
  /**
   * `DELETE /billing/allocations/{sourceType}/{id}` — release one allocation, addressed as the API
   * lists it: `sourceType` (1 payment, 2 credit) plus the allocation's own `id`. The money returns
   * to the source's unallocated remainder and nothing is re-applied.
   */
  async remove(sourceType, id) {
    await this.httpDELETE({ url: this.appendUrl(`/${sourceType}/${id}`) });
  }
  /**
   * `POST /billing/allocations/apply-available-funds` — fund one order from the account's
   * unallocated money, oldest source first whatever its kind: an older payment is spent before a
   * newer credit, and only a tie on the date goes to the credit (`AllocationsServiceTests`:
   * `UsesTheOldestSourceFirst_WhateverItsKind`, `OnTheSameDate_UsesTheCreditFirst`). It stops as
   * soon as the order is covered; the result reports what each side contributed and what is left.
   */
  async applyAvailableFunds(request) {
    return await this.httpPOST({
      url: this.appendUrl("/apply-available-funds"),
      body: request
    });
  }
  /**
   * `POST /billing/allocations/reallocate` — re-run allocation across a whole account,
   * optionally clearing its existing allocations first.
   */
  async reallocate(request) {
    return await this.httpPOST({
      url: this.appendUrl("/reallocate"),
      body: request
    });
  }
}
class LedgersClient extends RestClient {
  #payments;
  constructor(baseUrl) {
    super(baseUrl, "billing/ledgers");
    this.#payments = new PaymentsClient(baseUrl);
  }
  /**
   * `GET /billing/ledgers?customerId=&direction=` — a customer's ledger entries with running
   * balance. Newest first unless `direction` is given.
   */
  async getByCustomer(customerId, direction) {
    const query = { customerId: String(customerId) };
    if (direction !== void 0) query.direction = String(direction);
    return await this.get(query);
  }
  /**
   * `GET /billing/ledgers/related-entries?type=&entryId=` — the entries linked to one ledger
   * entry (e.g. the payments applied to an invoice). `type` is the entry's ledger `type` string.
   */
  async getRelatedEntries(type, entryId) {
    return this.#relatedEntries(type, entryId);
  }
  /**
   * The refunds raised against a payment, as full payment records. Found through
   * `related-entries`, which links legacy payments too (those carry no `accountId`, so the
   * account's payments cannot name them), then read one by one.
   *
   * Goes through the private read, not `getRelatedEntries`: under `reactive.service` the public
   * methods become actions, and a call to one from inside another would dispatch it — the ledger
   * listens to `getRelatedEntries` for its row highlight, which a refund lookup must not trigger.
   * The payments are read with a plain client for the same reason.
   */
  async getRefunds(paymentId) {
    const related = await this.#relatedEntries("Payment", paymentId);
    const refunds = (related ?? []).filter((entry) => entry.type === "Refund");
    return Promise.all(refunds.map((entry) => this.#payments.getById(entry.id)));
  }
  async #relatedEntries(type, entryId) {
    return await this.httpGET({
      url: this.appendUrl("/related-entries"),
      query: { type, entryId: String(entryId) }
    });
  }
  /** `GET /billing/ledgers/summary?customerId=` — unpaid and unallocated totals for a customer. */
  async getSummary(customerId) {
    return await this.httpGET({
      url: this.appendUrl("/summary"),
      query: { customerId: String(customerId) }
    });
  }
}
class PaymentPlanPaymentsClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "collections/payment-plan-payments");
  }
  /**
   * `POST /collections/payment-plan-payments` — put the payment on the plan. A pending payment is
   * settled on the plan when the processor answers. Rejects with a 400 when the payment cannot go
   * on the plan (for example, it is for more than the plan still owes).
   */
  async link(request) {
    return await this.create(request);
  }
}
const BillingConfig = EnvironmentConfigurations.hydrate({
  development: {
    host: "https://localhost:9002",
    api: {
      host: "https://localhost:7104",
      apiKey: "BillingAPI"
    }
  },
  staging: {
    host: "https://lf-artemis-staging.azurewebsites.net",
    api: {
      host: "https://lf-artemis-api-staging.azurewebsites.net",
      apiKey: "BillingAPI"
    }
  },
  production: {
    host: [
      "https://artemis.lifebeacon.com",
      "https://app.alert1.com",
      "https://app.lifebeacon.com"
    ],
    api: {
      host: "https://artemis.lfapi.net",
      apiKey: "BillingAPI"
    }
  }
});
const LedgersService = reactive.service(
  "ledgers",
  LedgersClient
)((Service) => new Service(BillingConfig.current.api.host));
LedgersService.reactor("ledgersReactor", {
  error: (response) => appRuntime.services.alerts.error(response.payload?.message ?? "Error")
});
const AllocationsService = reactive.service(
  "allocations",
  AllocationsClient
)((Service) => new Service(BillingConfig.current.api.host));
const alerts = () => appRuntime.services.alerts;
const reportApplied = (applied, remainingBalance, { none, some }) => {
  if (applied === 0) return alerts().info(none);
  const due = remainingBalance > 0 ? `, ${formatCurrencyUSD(remainingBalance)} still due` : "";
  return alerts().success(`${some(formatCurrencyUSD(applied))}${due}.`);
};
AllocationsService.reactor("allocationsReactor", {
  // The two commands are the ones whose outcome the screen cannot show on its own: when nothing
  // is applied no figure moves, so the alert says what the API did.
  success: (response) => {
    const { source, args, payload } = response;
    const { creditsApplied = 0, paymentsApplied = 0, remainingBalance = 0 } = payload ?? {};
    const applied = creditsApplied + paymentsApplied;
    switch (source?.action) {
      case "applyAvailableFunds":
        return reportApplied(applied, remainingBalance, {
          none: "This account has no unallocated funds to apply.",
          some: (amount) => `Applied ${amount}`
        });
      case "reallocate": {
        const rebuilt = args?.[0]?.removeExistingAllocations === true;
        const orders = payload?.ordersFunded ?? 0;
        return reportApplied(applied, remainingBalance, {
          none: rebuilt ? "Ledger rebuilt: there were no funds to allocate." : "This account has no unallocated funds to allocate.",
          some: (amount) => `${rebuilt ? "Ledger rebuilt: applied" : "Allocated"} ${amount} to ${orders} order${orders === 1 ? "" : "s"}`
        });
      }
      default:
        return void 0;
    }
  },
  error: (response) => alerts().error(response.payload?.message ?? "Error")
});
const CreditsService = reactive.service(
  "credits",
  CreditsClient
)((Service) => new Service(BillingConfig.current.api.host));
CreditsService.queryCredits.latest();
CreditsService.reactor("creditsReactor", {
  success: (response, _services, db2) => {
    const { source, payload } = response;
    if (source !== CreditsService.queryCredits) return;
    const { values, totalCount } = payload;
    RequestMetadata.setCount(source.action, totalCount);
    return replaceRows(db2, db2.credits, values);
  },
  error: (response) => {
    if (response.source?.action === "getById" && response.payload?.statusCode === 404) return;
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
const PaymentsService = reactive.service(
  "payments",
  PaymentsClient
)((Service) => new Service(BillingConfig.current.api.host));
PaymentsService.reactor("paymentsReactor", {
  success: (response) => {
    const action = response.source?.action;
    if (action === "queue") {
      appRuntime.services.alerts.success(`Payment #${response.payload?.id} sent to the processor.`);
    } else if (action === "dispute") {
      appRuntime.services.alerts.success(`Payment #${response.payload?.id} marked as disputed.`);
    } else if (action === "resolveDispute") {
      const { payment, charge } = response.payload ?? {};
      appRuntime.services.alerts.success(
        charge ? `Dispute on payment #${payment?.id} resolved. Payment #${charge.id} charges the customer.` : `Dispute on payment #${payment?.id} resolved.`
      );
    }
  },
  error: (response) => {
    if (response.source?.action === "getById" && response.payload?.statusCode === 404) return;
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
const PaymentPlanPaymentsService = reactive.service(
  "paymentPlanPayments",
  PaymentPlanPaymentsClient
)((Service) => new Service(BillingConfig.current.api.host));
PaymentPlanPaymentsService.reactor("paymentPlanPaymentsReactor", {
  success: (response) => {
    const entry = response.payload;
    appRuntime.services.alerts.success(
      `Payment #${entry?.paymentId} added to payment plan #${entry?.paymentPlanId}.`
    );
  },
  // The payment itself was recorded; only the link failed, and the payment's detail can retry it.
  error: (response) => {
    const request = response.args?.[0];
    appRuntime.services.alerts.error(
      `Payment #${request?.paymentId} was recorded but not added to payment plan #${request?.paymentPlanId}: ${response.payload?.message ?? "Error"} Add it from the payment's detail.`
    );
  }
});
const storeFormat = (store) => Object.entries(store).reduce((result, [key, value]) => {
  result[key] = value.join(",");
  return result;
}, {});
const db = new DexieDatabaseProvider([dexieRequestBrokerAddon]);
const ReactiveConfig = {
  db: {
    name: "livefree.billing.db",
    provider: db,
    init: (db2) => {
      db2.version(1).stores(storeFormat(_store));
      db2.version(2).stores({ credits: "&id, accountId" });
    }
  }
};
const _store = {
  // Payments list (queryMql results).
  payments: ["&id"],
  // Credits list: the current page of `queryCredits`.
  credits: ["&id", "accountId"]
};
const render = reactive.configure(ReactiveConfig).render;
const Shell = ({ children }) => /* @__PURE__ */ jsx("div", { className: "p-4 h-full flex flex-col", children });
let Bootstrapped;
const AppLayout = render(({ children }) => {
  Bootstrapped ??= App.bootstrap(Shell, { runtime: appRuntime });
  return /* @__PURE__ */ jsx(Bootstrapped, { children });
});
const CreditReasonField = reactive.component(
  ({ services, monitors, ...rest }) => {
    const [reasons, setReasons] = useState([]);
    useSuccessReactor(services.credits.getReasons, (payload) => setReasons(payload ?? []));
    return /* @__PURE__ */ jsx(
      SelectField,
      {
        ...rest,
        type: "number",
        items: reasons,
        isLoading: monitors.getReasons.executing,
        children: (reason) => /* @__PURE__ */ jsxs(ListBox.Item, { id: reason.id, textValue: reason.name, children: [
          reason.name,
          /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
        ] }, reason.id)
      }
    );
  },
  {
    name: "CreditReasonField",
    sync: (services) => [{ execute: () => services.credits.getReasons(), deps: [] }],
    monitors: (services) => [services.credits.getReasons]
  }
);
const CreditCreate = reactive.component(
  ({ services, monitors, accountId, onSuccess }) => {
    const [form, setForm] = useState({ isValid: false, data: {} });
    const [today] = useState(() => (/* @__PURE__ */ new Date()).toISOString());
    const LinkedAccountNotice = useComponent("accounts.linked-account-notice");
    useSuccessReactor(services.credits.submit, (payload, args) => {
      if (Number(args[0]?.accountId) === Number(accountId)) onSuccess?.(payload);
    });
    return /* @__PURE__ */ jsxs(Uniformly, { className: "flex flex-col gap-3", onChange: setForm, children: [
      /* @__PURE__ */ jsx(LinkedAccountNotice, { accountId: Number(accountId) }),
      /* @__PURE__ */ jsx(
        CreditReasonField,
        {
          name: "reason",
          label: "Reason",
          isRequired: true,
          validation: (v) => v.number().required("Reason is required")
        }
      ),
      /* @__PURE__ */ jsx(
        TextField,
        {
          name: "amount",
          label: "Amount",
          type: "string",
          isRequired: true,
          startContent: "$",
          transform: "float",
          mask: masks.numberWithDecimalsRegex,
          validation: (v) => v.number().required("Amount is required").positive("Amount must be more than $0.00")
        }
      ),
      /* @__PURE__ */ jsx(DateField, { name: "effectiveOn", label: "Effective date", value: today }),
      /* @__PURE__ */ jsx(
        TextAreaField,
        {
          name: "note",
          label: "Note",
          validation: (v) => v.string().max(500, "Note cannot exceed 500 characters")
        }
      ),
      /* @__PURE__ */ jsx(DrawerActions, { children: /* @__PURE__ */ jsx(
        SaveButton,
        {
          label: "Create Credit",
          iconClass: "ri-add-line",
          withConfirm: true,
          isDisabled: !form.isValid,
          isLoading: monitors.submit.executing,
          body: form.isValid && /* @__PURE__ */ jsxs("div", { children: [
            "This will credit account ",
            /* @__PURE__ */ jsx("strong", { children: accountId }),
            " ",
            /* @__PURE__ */ jsx("strong", { children: formatCurrencyUSD(form.data.amount ?? 0) }),
            "."
          ] }),
          onPress: () => services.credits.submit({
            accountId: Number(accountId),
            reason: form.data.reason,
            amount: form.data.amount,
            note: form.data.note || null,
            effectiveOnUtc: form.data.effectiveOn ? toISO(form.data.effectiveOn) : null
          })
        }
      ) })
    ] });
  },
  {
    name: "CreditCreate",
    monitors: (services) => [services.credits.submit]
  }
);
const CreditCreateDrawer = withDrawerController(({ item, onSuccess }) => /* @__PURE__ */ jsx(CreditCreate, { accountId: item?.accountId, onSuccess }));
const COLLECTIONS_REASONS = [
  CreditReasonType.CollectionsAmount,
  CreditReasonType.CollectionsFee,
  CreditReasonType.DeviceRecoveryAmount,
  CreditReasonType.DeviceRecoveryFee,
  CreditReasonType.WriteOff
];
const fieldName = (reason) => `amount_${reason}`;
const amountOf = (data, reason) => Number(data[fieldName(reason)]) || 0;
const CollectionsCreditsCreate = reactive.component(
  ({ services, monitors, accountId, onSuccess }) => {
    const [form, setForm] = useState({ isValid: true, data: {} });
    const [reasons, setReasons] = useState([]);
    const [today] = useState(() => (/* @__PURE__ */ new Date()).toISOString());
    useSuccessReactor(services.credits.getReasons, (payload) => setReasons(payload ?? []));
    useSuccessReactor(services.credits.submitMany, (payload, args) => {
      if (Number(args[0]?.accountId) === Number(accountId)) onSuccess?.(payload);
    });
    const labelOf = (reason) => reasons.find((r) => Number(r.id) === reason)?.description ?? reasons.find((r) => Number(r.id) === reason)?.name ?? `Reason ${reason}`;
    const credits = COLLECTIONS_REASONS.map((reason) => ({
      reason,
      amount: amountOf(form.data, reason)
    })).filter((credit) => credit.amount > 0);
    const total = credits.reduce((sum, credit) => sum + credit.amount, 0);
    const canSubmit = form.isValid && credits.length > 0 && Boolean(form.data.effectiveOn);
    const submit = () => services.credits.submitMany({
      accountId: Number(accountId),
      credits: credits.map((credit) => ({
        reason: credit.reason,
        amount: credit.amount,
        note: form.data.note || null,
        effectiveOnUtc: toISO(form.data.effectiveOn)
      }))
    });
    return /* @__PURE__ */ jsxs(Uniformly, { className: "flex flex-col gap-3", onChange: setForm, children: [
      /* @__PURE__ */ jsx("p", { className: "text-xs text-muted", children: "Enter an amount for each credit that applies. Reasons left blank are not created." }),
      COLLECTIONS_REASONS.map((reason) => /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-1 items-center gap-2 md:grid-cols-2", children: [
        /* @__PURE__ */ jsx(Label, { className: "md:pt-1", children: labelOf(reason) }),
        /* @__PURE__ */ jsx(
          TextField,
          {
            name: fieldName(reason),
            "aria-label": labelOf(reason),
            type: "string",
            startContent: "$",
            transform: "float",
            mask: masks.numberWithDecimalsRegex
          }
        )
      ] }, reason)),
      /* @__PURE__ */ jsx(
        DateField,
        {
          name: "effectiveOn",
          label: "Effective date",
          description: "Applied to every credit created.",
          isRequired: true,
          value: today,
          validation: (v) => v.string().required("Effective date is required")
        }
      ),
      /* @__PURE__ */ jsx(
        TextAreaField,
        {
          name: "note",
          label: "Note",
          description: "Applied to every credit created.",
          validation: (v) => v.string().max(500, "Note cannot exceed 500 characters")
        }
      ),
      /* @__PURE__ */ jsx(DrawerActions, { children: /* @__PURE__ */ jsx(
        SaveButton,
        {
          label: "Add Credits",
          iconClass: "ri-add-line",
          withConfirm: true,
          isDisabled: !canSubmit,
          isLoading: monitors.submitMany.executing,
          body: canSubmit && /* @__PURE__ */ jsxs("div", { children: [
            "This will add ",
            /* @__PURE__ */ jsx("strong", { children: credits.length }),
            " ",
            credits.length === 1 ? "credit" : "credits",
            " totalling",
            " ",
            /* @__PURE__ */ jsx("strong", { children: formatCurrencyUSD(total) }),
            " to account",
            " ",
            /* @__PURE__ */ jsx("strong", { children: accountId }),
            "."
          ] }),
          onPress: submit
        }
      ) })
    ] });
  },
  {
    name: "CollectionsCreditsCreate",
    sync: (services) => [{ execute: () => services.credits.getReasons(), deps: [] }],
    monitors: (services) => [services.credits.submitMany, services.credits.getReasons]
  }
);
const CollectionsCreditsCreateDrawer = withDrawerController(({ item, onSuccess }) => /* @__PURE__ */ jsx(CollectionsCreditsCreate, { accountId: item?.accountId, onSuccess }));
const formatMoney = (n) => formatCurrencyUSD(Math.abs(n || 0));
const LEDGER_STATUS_TONE = {
  // Payment Status
  Pending: "warn",
  Paid: "active",
  Declined: "danger",
  Authorized: "info",
  PartiallyRefunded: "warn",
  Refunded: "warn",
  Voided: "warn",
  Error: "danger",
  CustomerRejected: "danger",
  // Order Status
  New: "neutral",
  Processing: "info",
  Shipped: "info",
  Hold: "warn",
  Completed: "active",
  ShipmentReceived: "active",
  Cancelled: "warn",
  Activated: "active",
  UnableToActivate: "warn",
  AssumedActivated: "active",
  Upgrading: "info",
  NoService: "danger",
  Fulfilled: "active",
  NonBillable: "warn",
  // Credit Status
  Approved: "active"
};
const ledgerTone = (status) => LEDGER_STATUS_TONE[status] ?? "neutral";
const STATUS_LABELS = {
  CustomerRejected: "Disputed"
};
const statusLabel = (status) => STATUS_LABELS[status] ?? status;
const paymentPlanLabel = (row) => row?.paymentPlanId != null ? `Payment plan #${row.paymentPlanId}` : null;
const relatedKey = (type, id) => `${type}:${id}`;
const PAYMENT_LIKE_TYPES = /* @__PURE__ */ new Set(["Payment", "Pre-Payment", "Refund"]);
const isPaymentLike = (type) => PAYMENT_LIKE_TYPES.has(type);
const PAYMENT_REFUNDABLE_STATUSES = [
  PaymentStatusType.Authorized,
  PaymentStatusType.Paid,
  PaymentStatusType.PartiallyRefunded,
  PaymentStatusType.PartiallyPaid,
  PaymentStatusType.CustomerRejected
];
const PAYMENT_DELETABLE_TYPES = [PaymentType.Check, PaymentType.MoneyOrder];
const isDeletablePayment = (payment) => !!payment && (PAYMENT_DELETABLE_TYPES.includes(payment.type) || payment.statusType === PaymentStatusType.Pending);
const isCardOrAch = (payment) => !!payment && (payment.type === PaymentType.CreditCard || payment.type === PaymentType.ACH);
const isPlanLinkable = (payment) => !!payment && (payment.statusType === PaymentStatusType.Paid || payment.type === PaymentType.ACH && payment.statusType === PaymentStatusType.Authorized) && isCardOrAch(payment);
const isDisputablePayment = (payment) => isCardOrAch(payment) && payment.statusType === PaymentStatusType.Paid && !payment.locked;
const isOpenDispute = (payment, history) => payment?.statusType === PaymentStatusType.CustomerRejected && history != null && !(history[0]?.previousStatus === PaymentStatusType.CustomerRejected && history[0]?.status === PaymentStatusType.CustomerRejected);
const isRefundPayment = (payment) => payment?.type === PaymentType.Refund;
const isOfficeCash = (payment) => !!payment && payment.type === PaymentType.Cash && !payment.remoteTransactionId;
const PAYMENT_QUEUEABLE_TYPES = [
  PaymentType.CreditCard,
  PaymentType.ACH,
  PaymentType.Check,
  PaymentType.MoneyOrder
];
const isPaymentDue = (payment) => !!payment?.paymentDateUtc && DateTime.fromISO(payment.paymentDateUtc, { zone: "utc" }) <= DateTime.utc();
const isScheduledPayment = (payment) => !!payment && payment.statusType === PaymentStatusType.Pending && !isPaymentDue(payment);
const isProcessablePayment = (payment) => !!payment && payment.statusType === PaymentStatusType.Pending && !payment.locked && PAYMENT_QUEUEABLE_TYPES.includes(payment.type) && isPaymentDue(payment);
const REFUND_REASONS = [
  "Within 30 Days",
  "Previously Received Device",
  "Customer Service",
  "Canceled Before Shipment",
  "Overcharge - LiveFree Error",
  "Death"
];
const isCountedRefund = (refund) => refund.type === PaymentType.Refund && ![PaymentStatusType.Declined, PaymentStatusType.Voided, PaymentStatusType.Error].includes(
  refund.statusType
);
const toCents = (amount) => Math.round((Number(amount) || 0) * 100);
const fromCents = (cents) => cents / 100;
const sumCents = (list) => (list ?? []).reduce((total, item) => total + toCents(item.amount), 0);
const sumAmounts = (list) => fromCents(sumCents(list));
const remainingAmount = (amount, taken) => fromCents(Math.max(0, toCents(amount) - toCents(taken)));
const refundableAmount = (payment, accountPayments) => {
  if (!isCardOrAch(payment) || isRefundPayment(payment) || accountPayments == null) return null;
  const refunds = accountPayments.filter(
    (p) => Number(p.parentPaymentId) === Number(payment.id) && isCountedRefund(p)
  );
  return remainingAmount(payment.amount, sumAmounts(refunds));
};
const creditAvailable = (credit, allocated) => {
  if (credit.status !== CreditStatusType.Approved) return 0;
  return allocated == null ? null : remainingAmount(credit.amount, allocated);
};
const CREDIT_STATUS_NAME = {
  [CreditStatusType.Pending]: "Pending",
  [CreditStatusType.Approved]: "Approved",
  [CreditStatusType.Declined]: "Declined"
};
const creditStatusName = (status) => CREDIT_STATUS_NAME[status] ?? null;
const fmtAmt = (n) => {
  const v = n || 0;
  const str = (v < 0 ? "-" : "") + formatMoney(v);
  const cls = v > 0 ? "text-[var(--pill-danger-fg)]" : v < 0 ? "text-[var(--pill-success-fg)]" : "text-[var(--pill-info-fg)]";
  return { str, cls };
};
const AmountCell = ({ n, bold }) => {
  const { str, cls } = fmtAmt(n);
  return /* @__PURE__ */ jsx("span", { className: `tabular-nums ${cls} ${bold ? "font-semibold" : "font-medium"}`, children: str });
};
const DetailSkeleton = ({ rows = 6 }) => /* @__PURE__ */ jsxs("div", { className: "flex flex-col", "aria-busy": "true", "aria-label": "Loading", children: [
  /* @__PURE__ */ jsx("div", { className: "grid grid-cols-3 gap-4 border-b border-[var(--border)] px-4 py-3", children: [0, 1, 2].map((i) => /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5", children: [
    /* @__PURE__ */ jsx(Skeleton, { className: "h-3 w-16 rounded" }),
    /* @__PURE__ */ jsx(Skeleton, { className: "h-6 w-20 rounded-md" })
  ] }, i)) }),
  /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-3 px-4 py-4", children: Array.from({ length: rows }, (_, i) => /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-[90px_1fr] gap-3", children: [
    /* @__PURE__ */ jsx(Skeleton, { className: "h-3 w-16 rounded" }),
    /* @__PURE__ */ jsx(Skeleton, { className: "h-3 w-40 rounded" })
  ] }, i)) })
] });
const DetailEmpty = ({ icon = "ri-inbox-line", title, description, className = "" }) => /* @__PURE__ */ jsx(EmptyState, { className: `py-8 ${className}`, children: /* @__PURE__ */ jsxs(EmptyState.Header, { children: [
  /* @__PURE__ */ jsx(EmptyState.Media, { variant: "icon", children: /* @__PURE__ */ jsx("i", { className: icon }) }),
  /* @__PURE__ */ jsx(EmptyState.Title, { children: title }),
  description && /* @__PURE__ */ jsx(EmptyState.Description, { children: description })
] }) });
const DetailNotFound = ({ entryType, id, onBack }) => /* @__PURE__ */ jsxs(EmptyState, { className: "py-10", children: [
  /* @__PURE__ */ jsxs(EmptyState.Header, { children: [
    /* @__PURE__ */ jsx(EmptyState.Media, { variant: "icon", children: /* @__PURE__ */ jsx("i", { className: "ri-file-search-line" }) }),
    /* @__PURE__ */ jsxs(EmptyState.Title, { children: [
      entryType,
      " #",
      id,
      " not found"
    ] }),
    /* @__PURE__ */ jsx(EmptyState.Description, { children: "There is no record with this ID. It may have been migrated from the legacy portal without its detail, or the link is stale." })
  ] }),
  onBack && /* @__PURE__ */ jsx(EmptyState.Content, { children: /* @__PURE__ */ jsx(Button, { size: "sm", variant: "secondary", onPress: onBack, children: "Back to ledger" }) })
] });
const DetailError = ({ onRetry }) => /* @__PURE__ */ jsxs(EmptyState, { className: "py-10", children: [
  /* @__PURE__ */ jsxs(EmptyState.Header, { children: [
    /* @__PURE__ */ jsx(EmptyState.Media, { variant: "icon", children: /* @__PURE__ */ jsx("i", { className: "ri-error-warning-line" }) }),
    /* @__PURE__ */ jsx(EmptyState.Title, { children: "Couldn't load this record" }),
    /* @__PURE__ */ jsx(EmptyState.Description, { children: "Nothing was changed. You can try again." })
  ] }),
  onRetry && /* @__PURE__ */ jsx(EmptyState.Content, { children: /* @__PURE__ */ jsxs(Button, { size: "sm", variant: "secondary", onPress: onRetry, children: [
    /* @__PURE__ */ jsx("i", { className: "ri-refresh-line mr-1" }),
    "Retry"
  ] }) })
] });
const AllocationsList = ({
  allocations,
  isLoading,
  emptyTitle = "No allocations",
  emptyCause,
  entryOf,
  onOpenEntry,
  onRemove
}) => {
  const rows = (allocations ?? []).map((allocation) => ({
    ...allocation,
    key: `${allocation.sourceType}:${allocation.id}`,
    target: entryOf(allocation)
  }));
  const user = useUser();
  const config = useConfig();
  const showRemove = typeof onRemove === "function" && user.hasPolicy(config?.api?.apiKey ?? "", "Payments", "Update");
  const columns2 = [
    { key: "entry", label: "Entry" },
    { key: "applied", label: "Applied" },
    { key: "amount", label: "Amount", align: "end" },
    ...showRemove ? [{ key: "actions", label: "", align: "center" }] : []
  ];
  const renderCell = (row, columnKey) => {
    switch (columnKey) {
      case "entry":
        return /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 flex-col", children: [
          /* @__PURE__ */ jsxs(
            Button,
            {
              variant: "light",
              color: "primary",
              onPress: () => onOpenEntry?.(row.target.type, row.target.id),
              className: "h-auto min-w-0 justify-start p-0 font-mono font-semibold",
              children: [
                row.target.type,
                " #",
                row.target.id
              ]
            }
          ),
          row.target.label && /* @__PURE__ */ jsx("span", { className: "truncate text-[12px] text-[var(--muted)]", children: row.target.label })
        ] });
      case "applied":
        return /* @__PURE__ */ jsx("span", { className: "tabular-nums text-[var(--muted)]", children: parseAndFormatDate(row.allocatedOnUtc) });
      case "amount":
        return /* @__PURE__ */ jsx(AmountCell, { n: row.amount, bold: true });
      case "actions":
        return /* @__PURE__ */ jsx(
          ConfirmableButton,
          {
            isIconOnly: true,
            size: "sm",
            variant: "light",
            color: "danger",
            confirmColor: "danger",
            iconClass: "ri-close-circle-line",
            label: "Remove allocation",
            "aria-label": "Remove allocation",
            body: /* @__PURE__ */ jsxs("div", { children: [
              "Remove this allocation of ",
              /* @__PURE__ */ jsx("strong", { children: formatMoney(row.amount) }),
              "? The order will show that amount as owed again."
            ] }),
            onPress: () => onRemove(row)
          }
        );
      default:
        return null;
    }
  };
  return /* @__PURE__ */ jsxs("div", { className: "relative", children: [
    /* @__PURE__ */ jsx(LoadingBar, { isLoading, label: "Loading allocations" }),
    rows.length === 0 ? isLoading ? /* @__PURE__ */ jsx("div", { className: "px-4 py-6 text-center text-[12.5px] text-[var(--fg-3)]", children: "Loading allocations…" }) : /* @__PURE__ */ jsx(DetailEmpty, { icon: "ri-git-branch-line", title: emptyTitle, description: emptyCause }) : /* @__PURE__ */ jsx(Table, { className: "w-full text-[13px]", children: /* @__PURE__ */ jsx(Table.ScrollContainer, { children: /* @__PURE__ */ jsxs(Table.Content, { "aria-label": "Allocations", children: [
      /* @__PURE__ */ jsx(Table.Header, { columns: columns2, children: (column) => /* @__PURE__ */ jsx(
        Table.Column,
        {
          id: column.key,
          align: column.align,
          isRowHeader: column.key === "entry",
          children: column.label
        },
        column.key
      ) }),
      /* @__PURE__ */ jsx(Table.Body, { items: rows, children: (row) => /* @__PURE__ */ jsx(Table.Row, { id: row.key, children: columns2.map((column) => /* @__PURE__ */ jsx(Table.Cell, { align: column.align, children: renderCell(row, column.key) }, `${row.key}-${column.key}`)) }, row.key) })
    ] }) }) })
  ] });
};
const DetailSection = ({ title, count, action, children, noPad = false }) => /* @__PURE__ */ jsxs(
  Card,
  {
    variant: "transparent",
    className: "rounded-none border-0 border-b border-[var(--border)] last:border-b-0",
    children: [
      /* @__PURE__ */ jsxs(Card.Header, { className: "flex-row items-center justify-between px-4 pt-3 pb-1", children: [
        /* @__PURE__ */ jsxs(Card.Title, { className: "text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[var(--fg-3)]", children: [
          title,
          count != null && /* @__PURE__ */ jsxs("span", { className: "ml-1 font-normal", children: [
            "(",
            count,
            ")"
          ] })
        ] }),
        action
      ] }),
      /* @__PURE__ */ jsx(Card.Content, { className: noPad ? "p-0" : "px-0 pt-0 pb-2", children })
    ]
  }
);
const toneClass$1 = { danger: "text-danger", success: "text-success" };
const StatStrip = ({ stats }) => /* @__PURE__ */ jsx(KPIGroup, { className: "rounded-none border-x-0 border-t-0 border-b border-[var(--border)]", children: stats.map((stat, index) => /* @__PURE__ */ jsxs(Fragment, { children: [
  index > 0 && /* @__PURE__ */ jsx(KPIGroup.Separator, {}),
  /* @__PURE__ */ jsxs(KPI, { className: "min-w-0 flex-1", children: [
    /* @__PURE__ */ jsx(KPI.Header, { children: /* @__PURE__ */ jsx(KPI.Title, { children: stat.label }) }),
    /* @__PURE__ */ jsx(KPI.Content, { children: stat.isLoading ? /* @__PURE__ */ jsx(Skeleton, { className: "h-7 w-24 rounded-md", "aria-label": "Loading" }) : /* @__PURE__ */ jsx(
      KPI.Value,
      {
        value: stat.value ?? 0,
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
        className: toneClass$1[stat.tone]
      }
    ) }),
    stat.actions && /* @__PURE__ */ jsx(KPI.Footer, { className: "mt-2 flex flex-wrap items-center gap-1.5", children: stat.actions })
  ] })
] }, stat.label)) });
const CreditGeneral = ({ credit, reasonName, reasonsLoaded, onOpenEntry }) => /* @__PURE__ */ jsxs("div", { className: "py-1", children: [
  /* @__PURE__ */ jsx(FieldRow, { label: "Credit #", children: credit.id }),
  /* @__PURE__ */ jsx(FieldRow, { label: "Created", children: parseAndFormatDateTime(credit.createdOnUtc) }),
  /* @__PURE__ */ jsx(FieldRow, { label: "Reason", children: reasonName ?? (reasonsLoaded ? `Reason ${credit.reason}` : "…") }),
  /* @__PURE__ */ jsx(FieldRow, { label: "Amount", children: formatMoney(credit.amount) }),
  /* @__PURE__ */ jsx(FieldRow, { label: "Status", children: creditStatusName(credit.status) ?? "—" }),
  credit.effectiveOnUtc && /* @__PURE__ */ jsx(FieldRow, { label: "Effective", children: parseAndFormatDate(credit.effectiveOnUtc) }),
  credit.note && /* @__PURE__ */ jsx(FieldRow, { label: "Note", children: credit.note }),
  credit.paymentId != null && /* @__PURE__ */ jsx(FieldRow, { label: "From refund", last: true, children: /* @__PURE__ */ jsxs(
    Button,
    {
      variant: "light",
      color: "primary",
      onPress: () => onOpenEntry?.("Refund", credit.paymentId),
      className: "h-auto min-w-0 p-0 font-mono font-semibold",
      children: [
        "Refund #",
        credit.paymentId
      ]
    }
  ) })
] });
const CreditAudit = ({ credit }) => /* @__PURE__ */ jsxs("div", { className: "py-1", children: [
  /* @__PURE__ */ jsxs(FieldRow, { label: "Created by", children: [
    credit.createdBy,
    " · ",
    parseAndFormatDateTime(credit.createdOnUtc)
  ] }),
  /* @__PURE__ */ jsxs(FieldRow, { label: "Modified by", last: true, children: [
    credit.modifiedBy,
    " · ",
    parseAndFormatDateTime(credit.modifiedOnUtc)
  ] })
] });
const ReasonField = ({ reasonRef }) => /* @__PURE__ */ jsxs(
  TextField$1,
  {
    "aria-label": "Reason for status change",
    className: "mt-3",
    onChange: (value) => {
      reasonRef.current = value;
    },
    children: [
      /* @__PURE__ */ jsx(Label, { children: "Reason" }),
      /* @__PURE__ */ jsx(TextArea, { variant: "secondary", placeholder: "Optional — kept with the status history" })
    ]
  }
);
const CreditStatusActions = reactive.component(
  ({ services, monitors, credit, onSuccess }) => {
    const reasonRef = useRef("");
    useSuccessReactor(services.credits.updateStatus, (updated, args) => {
      if (Number(args[0]) === Number(credit?.id)) onSuccess?.(updated);
    });
    if (!credit) return null;
    const update = (status) => {
      const reason = reasonRef.current?.trim();
      reasonRef.current = "";
      services.credits.updateStatus(credit.id, { status, reason: reason || null });
    };
    const isLoading = monitors.updateStatus.executing;
    const money = /* @__PURE__ */ jsx("strong", { children: formatMoney(credit.amount) });
    const decline = /* @__PURE__ */ jsx(
      ConfirmableButton,
      {
        withConfirm: true,
        variant: "primary",
        confirmColor: "danger",
        iconClass: "ri-close-circle-line",
        label: "Decline credit",
        isLoading,
        body: /* @__PURE__ */ jsxs("div", { children: [
          "Decline credit ",
          /* @__PURE__ */ jsxs("strong", { children: [
            "#",
            credit.id
          ] }),
          " for ",
          money,
          "? Its allocations are removed and the orders it settled will owe again.",
          /* @__PURE__ */ jsx(ReasonField, { reasonRef })
        ] }),
        onPress: () => update(CreditStatusType.Declined)
      }
    );
    const approve = /* @__PURE__ */ jsx(
      ConfirmableButton,
      {
        withConfirm: true,
        variant: "primary",
        confirmColor: "primary",
        iconClass: "ri-checkbox-circle-line",
        label: "Approve credit",
        isLoading,
        body: /* @__PURE__ */ jsxs("div", { children: [
          "Approve credit ",
          /* @__PURE__ */ jsxs("strong", { children: [
            "#",
            credit.id
          ] }),
          " for ",
          money,
          "? It is applied to the account's unpaid orders right away.",
          /* @__PURE__ */ jsx(ReasonField, { reasonRef })
        ] }),
        onPress: () => update(CreditStatusType.Approved)
      }
    );
    return /* @__PURE__ */ jsx(RequirePolicy, { resource: "Credits", operation: "Update", children: /* @__PURE__ */ jsxs(DrawerActions, { children: [
      credit.status !== CreditStatusType.Declined && decline,
      credit.status !== CreditStatusType.Approved && approve
    ] }) });
  },
  {
    name: "CreditStatusActions",
    monitors: (services) => [services.credits.updateStatus]
  }
);
const allocationsEmptyCause$1 = (credit) => {
  if (credit.status === CreditStatusType.Declined) return "Declined credits are never allocated.";
  if (credit.status === CreditStatusType.Pending) return "Approve this credit to allocate it.";
  return "Not applied to any order yet.";
};
const creditStats = (credit, allocated, available, canViewAllocations) => [
  { label: "Credit", value: credit.amount },
  {
    label: "Allocated",
    value: canViewAllocations ? allocated : null,
    isLoading: canViewAllocations && allocated == null
  },
  {
    label: "Available",
    value: canViewAllocations ? available : null,
    tone: available > 0 ? "success" : "neutral",
    isLoading: canViewAllocations && available == null
  }
];
const CreditDetail = reactive.component(
  ({
    services,
    monitors,
    creditId,
    canViewAllocations = false,
    onOpenEntry,
    onStatus,
    onChanged,
    onBack
  }) => {
    const id = Number(creditId);
    const [credit, setCredit] = useState(null);
    const [allocations, setAllocations] = useState(null);
    const [reasons, setReasons] = useState(null);
    const [failure, setFailure] = useState(null);
    const isMine = (args) => Number(args[0]) === id;
    useSuccessReactor(services.credits.getById, (record, args) => {
      if (!isMine(args)) return;
      setCredit(record);
      setFailure(null);
      onStatus?.(creditStatusName(record.status));
    });
    useErrorReactor(services.credits.getById, (error, args) => {
      if (!isMine(args)) return;
      setFailure({ notFound: error?.statusCode === 404 });
    });
    useSuccessReactor(services.credits.getAllocations, (list, args) => {
      if (isMine(args)) setAllocations(list ?? []);
    });
    useSuccessReactor(services.credits.getReasons, (list) => setReasons(list ?? []));
    useSuccessReactor(services.allocations.remove, (_payload, [sourceType, allocationId]) => {
      if (!allocations?.some((a) => a.sourceType === sourceType && a.id === allocationId)) return;
      services.credits.getAllocations(id);
      onChanged?.();
    });
    if (failure) {
      return failure.notFound ? /* @__PURE__ */ jsx(DetailNotFound, { entryType: "Account Credit", id, onBack }) : /* @__PURE__ */ jsx(DetailError, { onRetry: () => services.credits.getById(id) });
    }
    if (!credit) return /* @__PURE__ */ jsx(DetailSkeleton, {});
    const allocated = allocations == null ? null : sumAmounts(allocations);
    const available = creditAvailable(credit, allocated);
    const reasonName = reasons?.find((r) => Number(r.id) === Number(credit.reason))?.name;
    const onStatusChanged = (updated) => {
      setCredit(updated);
      onStatus?.(creditStatusName(updated.status));
      if (canViewAllocations) services.credits.getAllocations(id);
      onChanged?.();
    };
    return /* @__PURE__ */ jsxs(Fragment$1, { children: [
      /* @__PURE__ */ jsx(StatStrip, { stats: creditStats(credit, allocated, available, canViewAllocations) }),
      /* @__PURE__ */ jsx(DetailSection, { title: "General", noPad: true, children: /* @__PURE__ */ jsx(
        CreditGeneral,
        {
          credit,
          reasonName,
          reasonsLoaded: reasons != null,
          onOpenEntry
        }
      ) }),
      /* @__PURE__ */ jsx(DetailSection, { title: "Allocations", count: allocations?.length, noPad: true, children: canViewAllocations ? /* @__PURE__ */ jsx(
        AllocationsList,
        {
          allocations,
          isLoading: monitors.getAllocations.executing || monitors.remove.executing,
          emptyCause: allocationsEmptyCause$1(credit),
          entryOf: (allocation) => ({ type: "Invoice", id: allocation.orderId }),
          onOpenEntry,
          onRemove: (allocation) => services.allocations.remove(allocation.sourceType, allocation.id)
        }
      ) : /* @__PURE__ */ jsx(
        DetailEmpty,
        {
          icon: "ri-lock-line",
          title: "Allocations unavailable",
          description: "Viewing where a credit was applied requires permission to view payments."
        }
      ) }),
      /* @__PURE__ */ jsx(DetailSection, { title: "Audit", noPad: true, children: /* @__PURE__ */ jsx(CreditAudit, { credit }) }),
      /* @__PURE__ */ jsx(CreditStatusActions, { credit, onSuccess: onStatusChanged })
    ] });
  },
  {
    name: "CreditDetail",
    sync: (services, { creditId, canViewAllocations }) => [
      {
        execute: () => creditId && services.credits.getById(Number(creditId)),
        deps: [creditId]
      },
      {
        execute: () => canViewAllocations && creditId && services.credits.getAllocations(Number(creditId)),
        deps: [creditId, canViewAllocations]
      },
      { execute: () => services.credits.getReasons(), deps: [] }
    ],
    monitors: (services) => [
      services.credits.getById,
      services.credits.getAllocations,
      services.credits.getReasons,
      services.allocations.remove
    ]
  }
);
const StatusPill = ({ status }) => /* @__PURE__ */ jsx(Pill, { tone: ledgerTone(status), children: statusLabel(status) });
const LedgerEntryHeader = ({ title, status, canBack, onBack }) => /* @__PURE__ */ jsx("div", { className: "flex h-[50px] flex-none items-center border-b border-[var(--border)] px-4", children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
  canBack && /* @__PURE__ */ jsx(
    Button,
    {
      isIconOnly: true,
      size: "sm",
      variant: "light",
      "aria-label": "Back to previous entry",
      onPress: onBack,
      className: "h-7 w-7 min-w-7",
      children: /* @__PURE__ */ jsx("i", { className: "ri-arrow-left-line" })
    }
  ),
  /* @__PURE__ */ jsx("span", { className: "font-mono text-[11px] font-semibold text-primary", children: title }),
  status && /* @__PURE__ */ jsx(StatusPill, { status })
] }) });
const LedgerEntryFallback = ({ row }) => {
  if (!row) {
    return /* @__PURE__ */ jsx(
      DetailEmpty,
      {
        icon: "ri-file-list-3-line",
        title: "No detail available",
        description: "This entry is not in the loaded ledger, so only its reference is known."
      }
    );
  }
  return /* @__PURE__ */ jsxs("div", { className: "py-2", children: [
    /* @__PURE__ */ jsx(FieldRow, { label: "Date", children: parseAndFormatDate(row.entryDateUtc) }),
    /* @__PURE__ */ jsx(FieldRow, { label: "Time", children: parseAndFormatTime(row.entryDateUtc) }),
    /* @__PURE__ */ jsx(FieldRow, { label: "Type", children: row.type }),
    /* @__PURE__ */ jsx(FieldRow, { label: "Category", children: row.subType }),
    /* @__PURE__ */ jsx(FieldRow, { label: "Amount", children: /* @__PURE__ */ jsx(AmountCell, { n: row.amount, bold: row.type === "Invoice" }) }),
    /* @__PURE__ */ jsx(FieldRow, { label: "Effective amount", children: /* @__PURE__ */ jsx(AmountCell, { n: row.effectiveAmount, bold: true }) }),
    /* @__PURE__ */ jsx(FieldRow, { label: "Item balance", children: /* @__PURE__ */ jsx(AmountCell, { n: row.entryBalance }) }),
    /* @__PURE__ */ jsx(FieldRow, { label: "Running balance", last: true, children: /* @__PURE__ */ jsx(AmountCell, { n: row.runningBalance, bold: true }) })
  ] });
};
const canDeletePayment = (payment) => isDeletablePayment(payment);
const canRefundPayment = (payment, refundable) => !isRefundPayment(payment) && payment.correlationId != null && PAYMENT_REFUNDABLE_STATUSES.includes(payment.statusType) && (refundable == null || refundable > 0);
const PaymentActions = ({
  payment,
  refundable,
  history,
  isDeleting,
  onDelete,
  onProcess,
  onRefund,
  onDispute,
  onResolveDispute
}) => {
  const showDelete = canDeletePayment(payment);
  const showProcess = isProcessablePayment(payment);
  const showRefund = canRefundPayment(payment, refundable);
  const showDispute = isDisputablePayment(payment);
  const showResolve = isOpenDispute(payment, history);
  if (!showDelete && !showProcess && !showRefund && !showDispute && !showResolve) return null;
  return /* @__PURE__ */ jsxs(DrawerActions, { children: [
    showDelete && /* @__PURE__ */ jsx(RequirePolicy, { resource: "Payments", operation: "Delete", children: /* @__PURE__ */ jsx(
      DeleteButton,
      {
        className: "me-auto",
        isLoading: isDeleting,
        body: /* @__PURE__ */ jsxs("div", { children: [
          "Delete ",
          payment.typeName,
          " payment ",
          /* @__PURE__ */ jsxs("strong", { children: [
            "#",
            payment.id
          ] }),
          " for",
          " ",
          /* @__PURE__ */ jsx("strong", { children: formatMoney(payment.amount) }),
          "? Its allocations are released."
        ] }),
        onPress: onDelete
      }
    ) }),
    showProcess && // The same policy as the API's queue route (POST billing/payments/{id}/queue).
    /* @__PURE__ */ jsx(RequirePolicy, { resource: "Payments", operation: "Create", children: /* @__PURE__ */ jsx(
      ConfirmableButton,
      {
        withConfirm: true,
        variant: "primary",
        confirmColor: "primary",
        iconClass: "ri-send-plane-line",
        label: "Process now",
        body: /* @__PURE__ */ jsxs("div", { children: [
          "Send ",
          payment.typeName,
          " payment ",
          /* @__PURE__ */ jsxs("strong", { children: [
            "#",
            payment.id
          ] }),
          " for",
          " ",
          /* @__PURE__ */ jsx("strong", { children: formatMoney(payment.amount) }),
          " to the processor now?"
        ] }),
        onPress: onProcess
      }
    ) }),
    showDispute && /* @__PURE__ */ jsx(RequirePolicy, { resource: "Payments", operation: "Dispute", children: /* @__PURE__ */ jsx(
      ConfirmableButton,
      {
        withConfirm: false,
        variant: "danger-soft",
        iconClass: "ri-error-warning-line",
        label: "Dispute",
        onPress: onDispute
      }
    ) }),
    showResolve && /* @__PURE__ */ jsx(RequirePolicy, { resource: "Payments", operation: "Dispute", children: /* @__PURE__ */ jsx(
      ConfirmableButton,
      {
        withConfirm: false,
        variant: "secondary",
        iconClass: "ri-scales-3-line",
        label: "Resolve dispute",
        onPress: onResolveDispute
      }
    ) }),
    showRefund && /* @__PURE__ */ jsx(RequirePolicy, { resource: "Payments", operation: "Refund", children: /* @__PURE__ */ jsx(
      ConfirmableButton,
      {
        withConfirm: false,
        variant: "primary",
        iconClass: "ri-refund-2-line",
        label: "Process refund",
        onPress: onRefund
      }
    ) })
  ] });
};
const CheckFields = ({ payment }) => /* @__PURE__ */ jsxs(Fragment$1, { children: [
  payment.checkNumber && /* @__PURE__ */ jsx(FieldRow, { label: "Check #", children: payment.checkNumber }),
  payment.referenceCheckDateUtc && /* @__PURE__ */ jsx(FieldRow, { label: "Check date", children: parseAndFormatDateTime(payment.referenceCheckDateUtc) }),
  payment.referenceCheckAmount != null && /* @__PURE__ */ jsx(FieldRow, { label: "Check amount", children: formatMoney(payment.referenceCheckAmount) }),
  payment.referenceCheckPayerType && /* @__PURE__ */ jsx(FieldRow, { label: "Payer", children: payment.referenceCheckPayerType }),
  payment.isSplitCheck && /* @__PURE__ */ jsx(FieldRow, { label: "Split check", children: "Yes" })
] });
const PaymentGeneral = ({ payment, onOpenEntry }) => {
  const isRefund = isRefundPayment(payment);
  const isCheque = payment.type === PaymentType.Check || payment.type === PaymentType.MoneyOrder;
  return /* @__PURE__ */ jsxs("div", { className: "py-1", children: [
    /* @__PURE__ */ jsx(FieldRow, { label: "Payment #", children: payment.id }),
    /* @__PURE__ */ jsx(FieldRow, { label: "Payment date", children: parseAndFormatDateTime(payment.paymentDateUtc) }),
    /* @__PURE__ */ jsx(FieldRow, { label: "Type", children: payment.typeName }),
    /* @__PURE__ */ jsx(FieldRow, { label: "Status", children: statusLabel(payment.statusTypeName) }),
    /* @__PURE__ */ jsx(FieldRow, { label: "Amount", children: formatMoney(payment.amount) }),
    payment.profileName && /* @__PURE__ */ jsx(FieldRow, { label: "Payment profile", children: payment.profileName }),
    payment.remoteTransactionId && /* @__PURE__ */ jsx(FieldRow, { label: "Transaction #", children: /* @__PURE__ */ jsx("span", { className: "font-mono text-[12px]", children: payment.remoteTransactionId }) }),
    payment.remoteTransactionResult && /* @__PURE__ */ jsx(FieldRow, { label: isRefund ? "Reason" : "Result", children: payment.remoteTransactionResult }),
    isRefund && payment.parentPaymentId != null && /* @__PURE__ */ jsx(FieldRow, { label: "Original payment", children: /* @__PURE__ */ jsxs(
      Button,
      {
        variant: "light",
        color: "primary",
        onPress: () => onOpenEntry?.("Payment", payment.parentPaymentId),
        className: "h-auto min-w-0 p-0 font-mono font-semibold",
        children: [
          "Payment #",
          payment.parentPaymentId
        ]
      }
    ) }),
    isCheque && /* @__PURE__ */ jsx(CheckFields, { payment }),
    payment.completedOnUtc && /* @__PURE__ */ jsx(FieldRow, { label: "Completed", children: parseAndFormatDateTime(payment.completedOnUtc) }),
    /* @__PURE__ */ jsxs(FieldRow, { label: "Created", children: [
      payment.createdBy,
      " · ",
      parseAndFormatDateTime(payment.createdOnUtc)
    ] }),
    /* @__PURE__ */ jsxs(FieldRow, { label: "Modified", last: true, children: [
      payment.modifiedBy,
      " · ",
      parseAndFormatDateTime(payment.modifiedOnUtc)
    ] })
  ] });
};
const columns$2 = [
  { key: "refund", label: "Refund" },
  { key: "date", label: "Date" },
  { key: "status", label: "Status" },
  { key: "amount", label: "Amount", align: "end" }
];
const RelatedPayments = reactive.component(
  ({ services, monitors, paymentId, onOpenEntry }) => {
    const id = Number(paymentId);
    const [refunds, setRefunds] = useState(null);
    const [failure, setFailure] = useState(false);
    const isMine = (args) => Number(args[0]) === id;
    useSuccessReactor(services.ledgers.getRefunds, (list, args) => {
      if (!isMine(args)) return;
      setRefunds(list ?? []);
      setFailure(false);
    });
    useErrorReactor(services.ledgers.getRefunds, (_error, args) => {
      if (isMine(args)) setFailure(true);
    });
    const isLoading = !failure && (refunds == null || monitors.getRefunds.executing);
    const rows = (refunds ?? []).map((refund) => ({ ...refund, key: String(refund.id) }));
    const renderCell = (row, columnKey) => {
      switch (columnKey) {
        case "refund":
          return /* @__PURE__ */ jsxs(
            Button,
            {
              variant: "light",
              color: "primary",
              onPress: () => onOpenEntry?.("Refund", row.id),
              className: "h-auto min-w-0 justify-start p-0 font-mono font-semibold",
              children: [
                "Refund #",
                row.id
              ]
            }
          );
        case "date":
          return /* @__PURE__ */ jsx("span", { className: "tabular-nums text-[var(--muted)]", children: parseAndFormatDate(row.paymentDateUtc) });
        case "status":
          return /* @__PURE__ */ jsx(StatusPill, { status: row.statusTypeName });
        case "amount":
          return /* @__PURE__ */ jsx(AmountCell, { n: -Math.abs(row.amount), bold: true });
        default:
          return null;
      }
    };
    return /* @__PURE__ */ jsx(DetailSection, { title: "Refunds", count: refunds?.length, noPad: true, children: /* @__PURE__ */ jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsx(LoadingBar, { isLoading, label: "Loading refunds" }),
      failure ? /* @__PURE__ */ jsx(DetailError, { onRetry: () => services.ledgers.getRefunds(id) }) : rows.length === 0 ? isLoading ? /* @__PURE__ */ jsx("div", { className: "px-4 py-6 text-center text-[12.5px] text-[var(--fg-3)]", children: "Loading refunds…" }) : /* @__PURE__ */ jsx(
        DetailEmpty,
        {
          icon: "ri-refund-2-line",
          title: "No refunds",
          description: "Nothing has been refunded from this payment."
        }
      ) : /* @__PURE__ */ jsx(Table, { className: "w-full text-[13px]", children: /* @__PURE__ */ jsx(Table.ScrollContainer, { children: /* @__PURE__ */ jsxs(Table.Content, { "aria-label": "Refunds", children: [
        /* @__PURE__ */ jsx(Table.Header, { columns: columns$2, children: (column) => /* @__PURE__ */ jsx(
          Table.Column,
          {
            id: column.key,
            align: column.align,
            isRowHeader: column.key === "refund",
            children: column.label
          },
          column.key
        ) }),
        /* @__PURE__ */ jsx(Table.Body, { items: rows, children: (row) => /* @__PURE__ */ jsx(Table.Row, { id: row.key, children: columns$2.map((column) => /* @__PURE__ */ jsx(Table.Cell, { align: column.align, children: renderCell(row, column.key) }, `${row.key}-${column.key}`)) }, row.key) })
      ] }) }) })
    ] }) });
  },
  {
    name: "RelatedPayments",
    sync: (services, { paymentId }) => [
      {
        execute: () => paymentId && services.ledgers.getRefunds(Number(paymentId)),
        deps: [paymentId]
      }
    ],
    monitors: (services) => [services.ledgers.getRefunds]
  }
);
const REASON_ITEMS = REFUND_REASONS.map((name) => ({ id: name, name }));
const RefundCreate = reactive.component(
  ({ services, monitors, payment, refundable, onSuccess }) => {
    const max = refundable ?? payment?.amount ?? 0;
    const [form, setForm] = useState({ isValid: false, data: {} });
    useSuccessReactor(services.payments.refund, (refund, args) => {
      if (Number(args[0]?.paymentId) === Number(payment?.id)) onSuccess?.(refund);
    });
    if (!payment) return null;
    const submit = () => services.payments.refund({
      paymentId: payment.id,
      refundAmount: Number(form.data.refundAmount),
      refundReason: form.data.refundReason || null,
      includeCredit: form.data.includeCredit === true
    });
    return /* @__PURE__ */ jsxs(Uniformly, { className: "flex flex-col gap-3", onChange: setForm, children: [
      /* @__PURE__ */ jsxs("div", { className: "text-[13px] text-[var(--muted)]", children: [
        "Refunding payment ",
        /* @__PURE__ */ jsxs("strong", { className: "font-mono", children: [
          "#",
          payment.id
        ] }),
        payment.profileName ? ` on ${payment.profileName}` : "",
        ". Up to",
        " ",
        /* @__PURE__ */ jsx("strong", { children: formatCurrencyUSD(max) }),
        " can be refunded."
      ] }),
      /* @__PURE__ */ jsx(
        TextField,
        {
          name: "refundAmount",
          label: "Refund amount",
          type: "string",
          isRequired: true,
          startContent: "$",
          transform: "float",
          mask: masks.numberWithDecimalsRegex,
          value: Number(max).toFixed(2),
          validation: (v) => v.number().required("Refund amount is required").positive("Refund amount must be more than $0.00").max(max, `Refund amount can't exceed ${formatCurrencyUSD(max)}`)
        }
      ),
      /* @__PURE__ */ jsx(
        SelectField,
        {
          name: "refundReason",
          label: "Reason",
          isRequired: true,
          items: REASON_ITEMS,
          validation: (v) => v.string().required("Reason is required"),
          children: (item) => /* @__PURE__ */ jsxs(ListBox.Item, { id: item.id, textValue: item.name, children: [
            item.name,
            /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
          ] }, item.id)
        }
      ),
      /* @__PURE__ */ jsx(SwitchField, { name: "includeCredit", children: "Issue a credit to the customer for the amount refunded" }),
      /* @__PURE__ */ jsx(DrawerActions, { children: /* @__PURE__ */ jsx(
        SaveButton,
        {
          label: "Refund",
          iconClass: "ri-refund-2-line",
          withConfirm: true,
          confirmColor: "danger",
          isDisabled: !form.isValid,
          isLoading: monitors.refund.executing,
          body: form.isValid && /* @__PURE__ */ jsxs("div", { children: [
            "This will refund ",
            /* @__PURE__ */ jsx("strong", { children: formatCurrencyUSD(form.data.refundAmount ?? 0) }),
            " ",
            "of payment ",
            /* @__PURE__ */ jsxs("strong", { children: [
              "#",
              payment.id
            ] }),
            ". The refund is sent to the gateway and the payment's allocations are rebalanced when it completes."
          ] }),
          onPress: submit
        }
      ) })
    ] });
  },
  {
    name: "RefundCreate",
    monitors: (services) => [services.payments.refund]
  }
);
const RefundCreateDrawer = withDrawerController(({ item, onSuccess }) => /* @__PURE__ */ jsx(RefundCreate, { payment: item?.payment, refundable: item?.refundable, onSuccess }));
const REASON_MAX = 500;
const reasonValidation = (v) => v.string().trim().required("Reason is required").max(REASON_MAX, `Reason cannot be longer than ${REASON_MAX} characters`);
const DisputeCreate = reactive.component(
  ({ services, monitors, payment, onSuccess }) => {
    const [form, setForm] = useState({ isValid: false, data: {} });
    useSuccessReactor(services.payments.dispute, (disputed, args) => {
      if (Number(args[0]) === Number(payment?.id)) onSuccess?.(disputed);
    });
    if (!payment) return null;
    const money = /* @__PURE__ */ jsx("strong", { children: formatMoney(payment.amount) });
    const submit = () => services.payments.dispute(payment.id, { reason: (form.data.reason ?? "").trim() });
    return /* @__PURE__ */ jsxs(Uniformly, { className: "flex flex-col gap-3", onChange: setForm, children: [
      /* @__PURE__ */ jsxs(Alert, { status: "warning", children: [
        /* @__PURE__ */ jsx(Alert.Indicator, {}),
        /* @__PURE__ */ jsxs(Alert.Content, { children: [
          /* @__PURE__ */ jsx(Alert.Title, { children: "What a dispute does" }),
          /* @__PURE__ */ jsxs(Alert.Description, { children: [
            "Payment ",
            /* @__PURE__ */ jsxs("strong", { children: [
              "#",
              payment.id
            ] }),
            " for ",
            money,
            " is taken off the orders it paid, so they owe again, and the customer's active subscription is put on hold. The processor is not contacted."
          ] })
        ] })
      ] }),
      /* @__PURE__ */ jsx(
        TextAreaField,
        {
          name: "reason",
          label: "Reason",
          isRequired: true,
          placeholder: "For example: chargeback from the bank, customer rejected the charge",
          validation: reasonValidation
        }
      ),
      /* @__PURE__ */ jsx(DrawerActions, { children: /* @__PURE__ */ jsx(
        SaveButton,
        {
          label: "Mark disputed",
          iconClass: "ri-error-warning-line",
          withConfirm: true,
          confirmColor: "danger",
          isDisabled: !form.isValid,
          isLoading: monitors.dispute.executing,
          body: /* @__PURE__ */ jsxs("div", { children: [
            "Mark payment ",
            /* @__PURE__ */ jsxs("strong", { children: [
              "#",
              payment.id
            ] }),
            " for ",
            money,
            " as disputed?"
          ] }),
          onPress: submit
        }
      ) })
    ] });
  },
  {
    name: "DisputeCreate",
    monitors: (services) => [services.payments.dispute]
  }
);
const DisputeCreateDrawer = withDrawerController(({ item, onSuccess }) => /* @__PURE__ */ jsx(DisputeCreate, { payment: item?.payment, onSuccess }));
const DEFAULT_FEE = 15;
const OUTCOME_BUTTONS = [
  { key: DisputeOutcome.Won, label: "Won" },
  { key: DisputeOutcome.Lost, label: "Lost" }
];
const feeValidation = (v) => v.number().typeError("Fee must be a number").min(0, "Fee cannot be negative");
const DisputeResolve = reactive.component(
  ({ services, monitors, payment, onSuccess }) => {
    const [form, setForm] = useState({ isValid: false, data: {} });
    const PaymentProfileField = useComponent("accounts.payment-profiles-field");
    useSuccessReactor(services.payments.resolveDispute, (response, args) => {
      if (Number(args[0]) === Number(payment?.id)) onSuccess?.(response);
    });
    if (!payment) return null;
    const outcome = form.data.outcome ?? DisputeOutcome.Won;
    const isLost = outcome === DisputeOutcome.Lost;
    const recharge = form.data.recharge !== false && form.data.recharge !== "";
    const fee = Number(form.data.feeAmount ?? DEFAULT_FEE) || 0;
    const total = isLost ? (recharge ? Number(payment.amount) : 0) + fee : 0;
    const needsProfile = total > 0;
    const canSubmit = form.isValid && (!needsProfile || Boolean(form.data.paymentProfileId));
    const submit = () => services.payments.resolveDispute(payment.id, {
      outcome,
      reason: (form.data.reason ?? "").trim(),
      recharge: isLost && recharge,
      feeAmount: isLost ? fee : 0,
      paymentProfileId: needsProfile ? Number(form.data.paymentProfileId) : null
    });
    const summary = isLost ? needsProfile ? /* @__PURE__ */ jsxs("div", { children: [
      "Close the dispute as lost and charge the customer ",
      /* @__PURE__ */ jsx("strong", { children: formatMoney(total) }),
      fee > 0 ? ` (including a ${formatMoney(fee)} dispute fee)` : "",
      "? Payment",
      " ",
      /* @__PURE__ */ jsxs("strong", { children: [
        "#",
        payment.id
      ] }),
      " stays disputed."
    ] }) : /* @__PURE__ */ jsxs("div", { children: [
      "Close the dispute as lost without charging the customer? Payment",
      " ",
      /* @__PURE__ */ jsxs("strong", { children: [
        "#",
        payment.id
      ] }),
      " stays disputed and its orders still owe."
    ] }) : /* @__PURE__ */ jsxs("div", { children: [
      "Close the dispute as won? Payment ",
      /* @__PURE__ */ jsxs("strong", { children: [
        "#",
        payment.id
      ] }),
      " for",
      " ",
      /* @__PURE__ */ jsx("strong", { children: formatMoney(payment.amount) }),
      " becomes Paid and is applied to the open orders again."
    ] });
    return /* @__PURE__ */ jsxs(Uniformly, { className: "flex flex-col gap-3", onChange: setForm, children: [
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5", children: [
        /* @__PURE__ */ jsx(Label, { children: "Outcome" }),
        /* @__PURE__ */ jsx(
          ToggleButtonsField,
          {
            name: "outcome",
            value: DisputeOutcome.Won,
            initialValue: DisputeOutcome.Won,
            buttons: OUTCOME_BUTTONS
          }
        ),
        /* @__PURE__ */ jsx("span", { className: "text-xs text-[var(--muted)]", children: isLost ? "The money is gone. The payment stays disputed." : "The money came back. The payment is Paid again." })
      ] }),
      /* @__PURE__ */ jsx(TextAreaField, { name: "reason", label: "Reason", isRequired: true, validation: reasonValidation }),
      isLost && /* @__PURE__ */ jsxs(Fragment$1, { children: [
        /* @__PURE__ */ jsxs(SwitchField, { name: "recharge", value: true, children: [
          "Charge the customer the disputed ",
          formatMoney(payment.amount),
          " again"
        ] }),
        /* @__PURE__ */ jsx(
          TextField,
          {
            name: "feeAmount",
            label: "Dispute fee",
            type: "string",
            startContent: "$",
            transform: "float",
            mask: masks.numberWithDecimalsRegex,
            value: DEFAULT_FEE.toFixed(2),
            validation: feeValidation
          }
        ),
        /* @__PURE__ */ jsx("span", { className: "-mt-2 text-xs text-[var(--muted)]", children: "Charged as a Disputed Payment Fee order. Enter 0 for no fee." }),
        needsProfile && PaymentProfileField && payment.accountId != null && /* @__PURE__ */ jsx(
          PaymentProfileField,
          {
            accountId: Number(payment.accountId),
            name: "paymentProfileId",
            label: "Payment profile",
            placeholder: "Select a payment profile",
            isRequired: true,
            validation: (v) => v.number().required("Payment profile is required")
          }
        )
      ] }),
      /* @__PURE__ */ jsx(DrawerActions, { children: /* @__PURE__ */ jsx(
        SaveButton,
        {
          label: needsProfile ? `Resolve and charge ${formatMoney(total)}` : "Resolve dispute",
          iconClass: "ri-checkbox-circle-line",
          withConfirm: true,
          confirmColor: needsProfile ? "danger" : "primary",
          isDisabled: !canSubmit,
          isLoading: monitors.resolveDispute.executing,
          body: summary,
          onPress: submit
        }
      ) })
    ] });
  },
  {
    name: "DisputeResolve",
    monitors: (services) => [services.payments.resolveDispute]
  }
);
const DisputeResolveDrawer = withDrawerController(({ item, onSuccess }) => /* @__PURE__ */ jsx(DisputeResolve, { payment: item?.payment, onSuccess }));
const statusName = (status) => Object.keys(PaymentStatusType).find((key) => PaymentStatusType[key] === status);
const describe = (entry) => {
  if (entry.status === PaymentStatusType.CustomerRejected) {
    return entry.previousStatus === PaymentStatusType.CustomerRejected ? "Dispute lost" : "Disputed";
  }
  if (entry.previousStatus === PaymentStatusType.CustomerRejected) {
    return entry.status === PaymentStatusType.Paid ? "Dispute won" : "Dispute ended by a refund";
  }
  return "Status changed";
};
const PaymentStatusHistory = ({ history }) => {
  if (!history?.length) return null;
  return /* @__PURE__ */ jsx(DetailSection, { title: "Status history", count: history.length, children: /* @__PURE__ */ jsx("ul", { className: "flex flex-col", children: history.map((entry) => /* @__PURE__ */ jsxs(
    "li",
    {
      className: "flex flex-col gap-1 border-b border-[var(--border)] px-4 py-2 last:border-b-0",
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-3", children: [
          /* @__PURE__ */ jsx("span", { className: "text-[13px] font-medium", children: describe(entry) }),
          /* @__PURE__ */ jsx(StatusPill, { status: statusName(entry.status) })
        ] }),
        entry.reason && /* @__PURE__ */ jsx("span", { className: "text-[13px]", children: entry.reason }),
        /* @__PURE__ */ jsxs("span", { className: "text-xs text-[var(--muted)]", children: [
          parseAndFormatDateTime(entry.createdOnUtc),
          " · ",
          entry.createdBy
        ] })
      ]
    },
    entry.id
  )) }) });
};
const TRANSACTION_DETAIL = "transactions.transaction-detail";
const PAYMENT_PLAN_NOTICE = "collections.payment-plan-notice";
const PAYMENT_PLAN_LINK = "collections.payment-plan-link";
const allocationsEmptyCause = (payment) => {
  if (payment.statusType === PaymentStatusType.Pending) return "Only paid payments are allocated.";
  if (payment.statusType === PaymentStatusType.Declined)
    return "Declined payments are never allocated.";
  if (payment.statusType === PaymentStatusType.CustomerRejected)
    return "A disputed payment is taken off the orders it paid.";
  return "Not applied to any order yet.";
};
const paymentStats = (payment, allocated, refundable) => {
  const stats = [{ label: "Amount", value: payment.amount }];
  if (!isRefundPayment(payment)) {
    stats.push({ label: "Allocated", value: allocated, isLoading: allocated == null });
  }
  if (isCardOrAch(payment) && !isRefundPayment(payment)) {
    stats.push({
      label: "Refundable",
      value: refundable,
      tone: refundable > 0 ? "success" : "neutral",
      isLoading: refundable == null
    });
  }
  return stats;
};
const isAwaitingProcessor = (payment) => payment.statusType === PaymentStatusType.Pending && !payment.remoteTransactionId;
const PaymentDetail = reactive.component(
  ({ services, monitors, paymentId, onOpenEntry, onStatus, onChanged, onSuccess, onBack }) => {
    const id = Number(paymentId);
    const [payment, setPayment] = useState(null);
    const [allocations, setAllocations] = useState(null);
    const [accountPayments, setAccountPayments] = useState(null);
    const [history, setHistory] = useState(null);
    const [failure, setFailure] = useState(null);
    const isMine = (args) => Number(args[0]) === id;
    const accountIdRef = useRef(null);
    const { components } = useRuntimeProvider();
    const TransactionDetail = components.get(TRANSACTION_DETAIL);
    const PaymentPlanNotice = components.get(PAYMENT_PLAN_NOTICE);
    const PaymentPlanLink = components.get(PAYMENT_PLAN_LINK);
    useSuccessReactor(services.payments.getById, (record, args) => {
      if (!isMine(args)) return;
      setPayment(record);
      setFailure(null);
      onStatus?.(record.statusTypeName);
      if (record.refundableAmount == null && isCardOrAch(record) && !isRefundPayment(record) && record.accountId != null) {
        accountIdRef.current = Number(record.accountId);
        services.payments.getByAccount(Number(record.accountId));
      }
    });
    useErrorReactor(services.payments.getById, (error, args) => {
      if (!isMine(args)) return;
      setFailure({ notFound: error?.statusCode === 404 });
    });
    useSuccessReactor(services.payments.getAllocations, (list, args) => {
      if (isMine(args)) setAllocations(list ?? []);
    });
    useSuccessReactor(services.payments.getStatusHistory, (list, args) => {
      if (isMine(args)) setHistory(list ?? []);
    });
    useSuccessReactor(services.payments.getByAccount, (list, args) => {
      if (Number(args[0]) === accountIdRef.current) setAccountPayments(list ?? []);
    });
    useSuccessReactor(services.payments.remove, (_payload, args) => {
      if (isMine(args)) onSuccess?.();
    });
    useSuccessReactor(services.payments.queue, (_payload, args) => {
      if (!isMine(args)) return;
      services.payments.getById(id);
      onChanged?.();
    });
    useSuccessReactor(services.allocations.remove, (_payload, [sourceType, allocationId]) => {
      if (!allocations?.some((a) => a.sourceType === sourceType && a.id === allocationId)) return;
      services.payments.getAllocations(id);
      onChanged?.();
    });
    const [refundDrawer, RefundDrawer] = useDrawerController(RefundCreateDrawer, {
      // Required: withDrawerController defaults isOpen to true.
      isOpen: false,
      size: "sm",
      headerContent: () => "Process refund",
      onSuccess: (refund) => onSuccess?.(refund)
    });
    const reloadAfterDispute = () => {
      services.payments.getById(id);
      services.payments.getAllocations(id);
      services.payments.getStatusHistory(id);
      onChanged?.();
    };
    const [disputeDrawer, DisputeDrawer] = useDrawerController(DisputeCreateDrawer, {
      isOpen: false,
      size: "sm",
      headerContent: () => "Mark disputed",
      onSuccess: reloadAfterDispute
    });
    const [resolveDrawer, ResolveDrawer] = useDrawerController(DisputeResolveDrawer, {
      isOpen: false,
      // The Lost path can show Accounts' profile picker.
      size: "md",
      headerContent: () => "Resolve dispute",
      onSuccess: reloadAfterDispute
    });
    if (failure) {
      return failure.notFound ? /* @__PURE__ */ jsx(DetailNotFound, { entryType: "Payment", id, onBack }) : /* @__PURE__ */ jsx(DetailError, { onRetry: () => services.payments.getById(id) });
    }
    if (!payment) return /* @__PURE__ */ jsx(DetailSkeleton, {});
    const allocated = allocations == null ? null : sumAmounts(allocations);
    const refundable = isCardOrAch(payment) && !isRefundPayment(payment) ? payment.refundableAmount ?? refundableAmount(payment, accountPayments) : null;
    const general = /* @__PURE__ */ jsxs(Fragment$1, { children: [
      /* @__PURE__ */ jsx(PaymentGeneral, { payment, onOpenEntry }),
      /* @__PURE__ */ jsx(PaymentStatusHistory, { history }),
      isCardOrAch(payment) && !isRefundPayment(payment) && /* @__PURE__ */ jsx(RelatedPayments, { paymentId: id, onOpenEntry }),
      TransactionDetail && payment.correlationId && /* @__PURE__ */ jsx(DetailSection, { title: "Transaction", noPad: true, children: isOfficeCash(payment) ? /* @__PURE__ */ jsx(
        DetailEmpty,
        {
          icon: "ri-money-dollar-box-line",
          title: "Paid in cash at the office",
          description: "Cash is settled when it is taken, so there is no processor transaction for this payment."
        }
      ) : isAwaitingProcessor(payment) ? /* @__PURE__ */ jsx(
        DetailEmpty,
        {
          icon: "ri-time-line",
          title: "Not sent to the processor yet",
          description: "The transaction appears here once the payment has been processed."
        }
      ) : /* @__PURE__ */ jsx(TransactionDetail, { transactionId: payment.correlationId }) })
    ] });
    return /* @__PURE__ */ jsxs(Fragment$1, { children: [
      /* @__PURE__ */ jsx(StatStrip, { stats: paymentStats(payment, allocated, refundable) }),
      isScheduledPayment(payment) && /* @__PURE__ */ jsxs(Alert, { status: "accent", className: "m-4", children: [
        /* @__PURE__ */ jsx(Alert.Indicator, {}),
        /* @__PURE__ */ jsxs(Alert.Content, { children: [
          /* @__PURE__ */ jsx(Alert.Title, { children: "Scheduled" }),
          /* @__PURE__ */ jsxs(Alert.Description, { children: [
            "This payment will be sent to the processor on",
            " ",
            parseAndFormatDateTime(payment.paymentDateUtc),
            "."
          ] })
        ] })
      ] }),
      PaymentPlanNotice && /* @__PURE__ */ jsx(PaymentPlanNotice, { paymentId: id, className: "m-4" }),
      PaymentPlanLink && isPlanLinkable(payment) && payment.accountId != null && /* @__PURE__ */ jsx(
        PaymentPlanLink,
        {
          paymentId: id,
          customerId: Number(payment.accountId),
          amount: payment.amount,
          onLinked: () => onChanged?.(),
          className: "m-4"
        }
      ),
      isRefundPayment(payment) ? general : /* @__PURE__ */ jsxs(Tabs, { variant: "secondary", className: "px-0", children: [
        /* @__PURE__ */ jsx(Tabs.ListContainer, { children: /* @__PURE__ */ jsxs(Tabs.List, { "aria-label": "Payment detail", className: "px-4", children: [
          /* @__PURE__ */ jsxs(Tabs.Tab, { id: "general", children: [
            "General",
            /* @__PURE__ */ jsx(Tabs.Indicator, {})
          ] }),
          /* @__PURE__ */ jsxs(Tabs.Tab, { id: "allocations", children: [
            "Allocations",
            allocations != null ? ` (${allocations.length})` : "",
            /* @__PURE__ */ jsx(Tabs.Indicator, {})
          ] })
        ] }) }),
        /* @__PURE__ */ jsx(Tabs.Panel, { id: "general", children: general }),
        /* @__PURE__ */ jsx(Tabs.Panel, { id: "allocations", children: /* @__PURE__ */ jsx(
          AllocationsList,
          {
            allocations,
            isLoading: monitors.getAllocations.executing || monitors.remove.executing,
            emptyCause: allocationsEmptyCause(payment),
            entryOf: (allocation) => ({ type: "Invoice", id: allocation.orderId }),
            onOpenEntry,
            onRemove: (allocation) => services.allocations.remove(allocation.sourceType, allocation.id)
          }
        ) })
      ] }),
      /* @__PURE__ */ jsx(
        PaymentActions,
        {
          payment,
          refundable,
          history,
          isDeleting: monitors.remove.executing,
          onDelete: () => services.payments.remove(id),
          onProcess: () => services.payments.queue(id),
          onRefund: () => refundDrawer.open({ payment, refundable }),
          onDispute: () => disputeDrawer.open({ payment }),
          onResolveDispute: () => resolveDrawer.open({ payment })
        }
      ),
      /* @__PURE__ */ jsx(RefundDrawer, {}),
      /* @__PURE__ */ jsx(DisputeDrawer, {}),
      /* @__PURE__ */ jsx(ResolveDrawer, {})
    ] });
  },
  {
    name: "PaymentDetail",
    sync: (services, { paymentId }) => [
      {
        execute: () => paymentId && services.payments.getById(Number(paymentId)),
        deps: [paymentId]
      },
      {
        execute: () => paymentId && services.payments.getAllocations(Number(paymentId)),
        deps: [paymentId]
      },
      {
        execute: () => paymentId && services.payments.getStatusHistory(Number(paymentId)),
        deps: [paymentId]
      }
    ],
    monitors: (services) => [
      services.payments.getById,
      services.payments.getAllocations,
      services.payments.getByAccount,
      services.payments.remove,
      services.allocations.remove
    ]
  }
);
const PaymentMethod = {
  Profile: "profile",
  Cash: "cash",
  Check: "check"
};
const METHOD_TABS = [
  { key: PaymentMethod.Profile, label: "Profile", icon: "ri-bank-card-line" },
  { key: PaymentMethod.Cash, label: "Cash", icon: "ri-money-dollar-box-line" },
  { key: PaymentMethod.Check, label: "Check", icon: "ri-file-list-3-line" }
];
const PAYER_TYPES = Object.values(CheckPayerType).map((name) => ({ id: name, name }));
const PAYMENT_PLAN_FIELD = "collections.payment-plan-field";
const planAmountError = (plan, amount) => plan && Number(amount) > Number(plan.amountRemaining) ? `More than the ${formatCurrencyUSD(plan.amountRemaining)} payment plan #${plan.id} still owes.` : null;
const PROFILE_PAYMENT_TYPES = { 1: PaymentType.CreditCard, 2: PaymentType.ACH };
const amountValidation = (v) => v.number().required("Amount is required").positive("Amount must be more than $0.00");
const AmountField = ({ name = "amount", label = "Amount", validation = amountValidation, value }) => /* @__PURE__ */ jsx(
  TextField,
  {
    name,
    label,
    value,
    type: "string",
    isRequired: validation === amountValidation,
    startContent: "$",
    transform: "float",
    mask: masks.numberWithDecimalsRegex,
    validation
  }
);
const PaymentDateField = ({ value }) => /* @__PURE__ */ jsx(
  DateTimeField,
  {
    name: "paymentDate",
    label: "Payment date",
    value,
    hourCycle: 12,
    description: "Defaults to right now."
  }
);
const PaymentCreate = reactive.component(
  ({ services, monitors, accountId, amount, invoiceBalance, availableCredit, onSuccess }) => {
    const [form, setForm] = useState({ isValid: false, data: {} });
    const [initialAmount] = useState(() => amount != null ? Number(amount).toFixed(2) : void 0);
    const [initialPaymentDate] = useState(() => toISO(DateTime.now()));
    const [profiles, setProfiles] = useState(null);
    const [profileId, setProfileId] = useState(void 0);
    const [addingProfile, setAddingProfile] = useState(false);
    const PaymentProfileField = useComponent("accounts.payment-profiles-field");
    const PaymentProfileCreate = useComponent("accounts.payment-profile-create");
    const PaymentPlanField = useComponent(PAYMENT_PLAN_FIELD);
    const LinkedAccountNotice = useComponent("accounts.linked-account-notice");
    const [plans, setPlans] = useState([]);
    const linkPlanIdRef = useRef(null);
    useSuccessReactor(services.payments.submit, (payload, args) => {
      if (Number(args[0]?.accountId) !== Number(accountId)) return;
      const paymentPlanId = linkPlanIdRef.current;
      linkPlanIdRef.current = null;
      if (paymentPlanId && payload?.id) {
        services.paymentPlanPayments.link({ paymentPlanId, paymentId: Number(payload.id) });
      }
      onSuccess?.(payload);
    });
    const method = form.data.method ?? PaymentMethod.Profile;
    const profile = (profiles ?? []).find((p) => Number(p.id) === Number(form.data.paymentProfileId));
    const profileType = profile ? PROFILE_PAYMENT_TYPES[profile.profileType] : void 0;
    const profileBlocked = method === PaymentMethod.Profile && profile && !profileType;
    const type = method === PaymentMethod.Cash ? PaymentType.Cash : method === PaymentMethod.Check ? PaymentType.Check : profileType;
    const canPickPlan = method === PaymentMethod.Profile && (profileType === PaymentType.CreditCard || profileType === PaymentType.ACH);
    const plan = canPickPlan ? plans.find((p) => Number(p.id) === Number(form.data.paymentPlanId)) : void 0;
    const amountError = planAmountError(plan, form.data.amount);
    const canSubmit = form.isValid && Boolean(type) && !profileBlocked && !amountError;
    const submit = () => {
      linkPlanIdRef.current = plan ? Number(plan.id) : null;
      services.payments.submit({
        accountId: Number(accountId),
        type,
        amount: form.data.amount,
        profileId: method === PaymentMethod.Profile ? Number(form.data.paymentProfileId) : null,
        // The date fields already hand back UTC ISO (their `convertDateOut` runs `toISO`); running
        // it again would read that UTC text as local time and shift it by the zone offset.
        paymentDateUtc: form.data.paymentDate || void 0,
        checkNumber: method === PaymentMethod.Check ? form.data.checkNumber : null,
        referenceCheckDateUtc: method === PaymentMethod.Check && form.data.checkDate ? form.data.checkDate : null,
        referenceCheckAmount: method === PaymentMethod.Check ? form.data.checkAmount ?? null : null,
        referenceCheckPayerType: method === PaymentMethod.Check ? form.data.checkPayerType || null : null,
        isSplitCheck: method === PaymentMethod.Check ? Boolean(form.data.isSplitCheck) : null
      });
    };
    const summary = {
      [PaymentMethod.Profile]: `charge ${profile?.name ?? "the selected profile"}`,
      [PaymentMethod.Cash]: "record a cash payment",
      [PaymentMethod.Check]: `record check #${form.data.checkNumber ?? ""}`
    }[method];
    return /* @__PURE__ */ jsxs(Fragment$1, { children: [
      /* @__PURE__ */ jsx(LinkedAccountNotice, { accountId: Number(accountId), className: "mb-3" }),
      addingProfile && /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3", children: [
        /* @__PURE__ */ jsx("span", { className: "text-[13px] font-medium", children: "New payment profile" }),
        /* @__PURE__ */ jsx(
          PaymentProfileCreate,
          {
            accountId: Number(accountId),
            onSuccess: (created) => {
              setProfileId(created?.id);
              setAddingProfile(false);
            },
            onCancel: () => setAddingProfile(false)
          }
        )
      ] }),
      /* @__PURE__ */ jsxs(Uniformly, { className: `flex flex-col gap-3${addingProfile ? " hidden" : ""}`, onChange: setForm, children: [
        availableCredit > 0 && /* @__PURE__ */ jsxs(Alert, { status: "accent", children: [
          /* @__PURE__ */ jsx(Alert.Indicator, {}),
          /* @__PURE__ */ jsxs(Alert.Content, { children: [
            /* @__PURE__ */ jsx(Alert.Title, { children: "Heads up" }),
            /* @__PURE__ */ jsxs(Alert.Description, { children: [
              "This customer has ",
              formatCurrencyUSD(availableCredit),
              " of account credit available."
            ] })
          ] })
        ] }),
        invoiceBalance != null && /* @__PURE__ */ jsxs("div", { className: "text-[13px] text-[var(--muted)]", children: [
          "Invoice balance: ",
          /* @__PURE__ */ jsx("strong", { children: formatCurrencyUSD(invoiceBalance) })
        ] }),
        /* @__PURE__ */ jsx(Label, { children: "Payment method" }),
        /* @__PURE__ */ jsxs(TabsField, { name: "method", value: PaymentMethod.Profile, children: [
          /* @__PURE__ */ jsx(Tabs.ListContainer, { children: /* @__PURE__ */ jsx(Tabs.List, { "aria-label": "Payment method", children: METHOD_TABS.map((tab) => /* @__PURE__ */ jsxs(Tabs.Tab, { id: tab.key, children: [
            /* @__PURE__ */ jsxs("span", { className: "flex items-baseline gap-1.5", children: [
              /* @__PURE__ */ jsx("i", { className: tab.icon }),
              /* @__PURE__ */ jsx("span", { children: tab.label })
            ] }),
            /* @__PURE__ */ jsx(Tabs.Indicator, {})
          ] }, tab.key)) }) }),
          /* @__PURE__ */ jsx(Tabs.Panel, { id: PaymentMethod.Profile, children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3 pt-3", children: [
            /* @__PURE__ */ jsx(
              PaymentProfileField,
              {
                accountId: Number(accountId),
                name: "paymentProfileId",
                label: "Payment profile",
                placeholder: "Select a payment profile",
                isRequired: true,
                value: profileId,
                validation: (v) => v.number().required("Payment profile is required"),
                onProfilesLoaded: setProfiles
              }
            ),
            /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-3", children: [
              /* @__PURE__ */ jsx("span", { className: "text-xs text-[var(--muted)]", children: profiles?.length === 0 ? "This customer has no payment profiles yet." : "" }),
              /* @__PURE__ */ jsxs(Button, { size: "sm", variant: "secondary", onPress: () => setAddingProfile(true), children: [
                /* @__PURE__ */ jsx("i", { className: "ri-add-line" }),
                " New profile"
              ] })
            ] }),
            profileBlocked && /* @__PURE__ */ jsx("p", { className: "text-xs text-danger", children: "This profile cannot be charged. Choose a card or bank account profile." }),
            /* @__PURE__ */ jsx(PaymentDateField, { value: initialPaymentDate }),
            /* @__PURE__ */ jsx(AmountField, { value: initialAmount }),
            canPickPlan && /* @__PURE__ */ jsx(
              PaymentPlanField,
              {
                customerId: Number(accountId),
                name: "paymentPlanId",
                description: "Optional. The plan counts this payment toward what it still owes.",
                onPlansLoaded: setPlans
              }
            ),
            amountError && /* @__PURE__ */ jsx("p", { className: "text-xs text-danger", children: amountError })
          ] }) }),
          /* @__PURE__ */ jsx(Tabs.Panel, { id: PaymentMethod.Cash, children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3 pt-3", children: [
            /* @__PURE__ */ jsx(PaymentDateField, { value: initialPaymentDate }),
            /* @__PURE__ */ jsx(AmountField, { value: initialAmount })
          ] }) }),
          /* @__PURE__ */ jsx(Tabs.Panel, { id: PaymentMethod.Check, children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3 pt-3", children: [
            /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-1 gap-3 md:grid-cols-2", children: [
              /* @__PURE__ */ jsx(
                TextField,
                {
                  name: "checkNumber",
                  label: "Check #",
                  isRequired: true,
                  validation: (v) => v.string().required("Check # is required").max(25, "Check # cannot exceed 25 characters")
                }
              ),
              /* @__PURE__ */ jsx(DateField, { name: "checkDate", label: "Check date" }),
              /* @__PURE__ */ jsx(
                AmountField,
                {
                  name: "checkAmount",
                  label: "Check amount",
                  validation: (v) => v.number().positive("Check amount must be more than $0.00")
                }
              ),
              /* @__PURE__ */ jsx(SelectField, { name: "checkPayerType", label: "Payer", items: PAYER_TYPES, children: (item) => /* @__PURE__ */ jsxs(ListBox.Item, { id: item.id, textValue: item.name, children: [
                item.name,
                /* @__PURE__ */ jsx(ListBox.ItemIndicator, {})
              ] }, item.id) })
            ] }),
            /* @__PURE__ */ jsx(SwitchField, { name: "isSplitCheck", children: "Split check (covers more than this payment)" }),
            /* @__PURE__ */ jsx(PaymentDateField, { value: initialPaymentDate }),
            /* @__PURE__ */ jsx(AmountField, { value: initialAmount })
          ] }) })
        ] }),
        !addingProfile && /* @__PURE__ */ jsx(DrawerActions, { children: /* @__PURE__ */ jsx(
          SaveButton,
          {
            label: "New Payment",
            iconClass: "ri-add-line",
            withConfirm: true,
            isDisabled: !canSubmit,
            isLoading: monitors.submit.executing,
            body: canSubmit && /* @__PURE__ */ jsxs("div", { children: [
              "This will ",
              summary,
              " for",
              " ",
              /* @__PURE__ */ jsx("strong", { children: formatCurrencyUSD(form.data.amount ?? 0) }),
              " on account",
              " ",
              /* @__PURE__ */ jsx("strong", { children: accountId }),
              plan && /* @__PURE__ */ jsxs(Fragment$1, { children: [
                ", and add it to payment plan ",
                /* @__PURE__ */ jsxs("strong", { children: [
                  "#",
                  plan.id
                ] })
              ] }),
              "."
            ] }),
            onPress: submit
          }
        ) })
      ] })
    ] });
  },
  {
    name: "PaymentCreate",
    monitors: (services) => [services.payments.submit]
  }
);
const PaymentCreateDrawer = withDrawerController(({ item, onSuccess }) => /* @__PURE__ */ jsx(
  PaymentCreate,
  {
    accountId: item?.accountId,
    amount: item?.amount,
    invoiceBalance: item?.invoiceBalance,
    availableCredit: item?.availableCredit,
    onSuccess
  }
));
const columns$1 = [
  { key: "payment", label: "Payment" },
  { key: "date", label: "Date" },
  { key: "status", label: "Status" },
  { key: "amount", label: "Amount", align: "end" }
];
const isPending = (payment) => payment?.statusType === PaymentStatusType.Pending;
const PendingPayments = reactive.component(
  ({ services, monitors, accountId, onOpenEntry }) => {
    const id = Number(accountId);
    const [pending, setPending] = useState(null);
    const [failure, setFailure] = useState(false);
    const isMine = (args) => Number(args[0]) === id;
    const isListed = (paymentId) => pending?.some((payment) => payment.id === Number(paymentId));
    const reload = () => services.payments.getByAccount(id);
    useSuccessReactor(services.payments.getByAccount, (list, args) => {
      if (!isMine(args)) return;
      setPending((list ?? []).filter(isPending));
      setFailure(false);
    });
    useErrorReactor(services.payments.getByAccount, (_error, args) => {
      if (isMine(args)) setFailure(true);
    });
    useSuccessReactor(services.payments.submit, (payment) => {
      if (Number(payment?.accountId) === id) reload();
    });
    useSuccessReactor(services.payments.remove, (_payload, [paymentId]) => {
      if (isListed(paymentId)) reload();
    });
    useSuccessReactor(services.payments.queue, (_payload, [paymentId]) => {
      if (isListed(paymentId)) reload();
    });
    if (failure) {
      return /* @__PURE__ */ jsx(DetailSection, { title: "Pending payments", noPad: true, children: /* @__PURE__ */ jsx(DetailError, { onRetry: reload }) });
    }
    if (!pending?.length) return null;
    const rows = pending.map((payment) => ({ ...payment, key: String(payment.id) }));
    const renderCell = (row, columnKey) => {
      switch (columnKey) {
        case "payment":
          return /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 flex-col", children: [
            /* @__PURE__ */ jsxs(
              Button,
              {
                variant: "light",
                color: "primary",
                onPress: () => onOpenEntry?.(isRefundPayment(row) ? "Refund" : "Payment", row.id),
                className: "h-auto min-w-0 justify-start p-0 font-mono font-semibold",
                children: [
                  isRefundPayment(row) ? "Refund" : "Payment",
                  " #",
                  row.id
                ]
              }
            ),
            row.typeName && /* @__PURE__ */ jsx("span", { className: "truncate text-[12px] text-[var(--muted)]", children: row.typeName })
          ] });
        case "date":
          return /* @__PURE__ */ jsx("span", { className: "tabular-nums text-[var(--muted)]", children: parseAndFormatDate(row.paymentDateUtc) });
        case "status":
          return /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 flex-col items-start gap-0.5", children: [
            /* @__PURE__ */ jsx(StatusPill, { status: row.statusTypeName }),
            isScheduledPayment(row) && /* @__PURE__ */ jsx("span", { className: "text-[12px] text-[var(--muted)]", children: "Scheduled" })
          ] });
        case "amount":
          return /* @__PURE__ */ jsx(AmountCell, { n: -Math.abs(row.amount), bold: true });
        default:
          return null;
      }
    };
    return /* @__PURE__ */ jsx(DetailSection, { title: "Pending payments", count: rows.length, noPad: true, children: /* @__PURE__ */ jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsx(
        LoadingBar,
        {
          isLoading: monitors.getByAccount.executing,
          label: "Loading pending payments"
        }
      ),
      /* @__PURE__ */ jsx("p", { className: "px-4 pb-2 text-[12px] text-[var(--muted)]", children: "Not applied yet: a pending payment is allocated once it settles." }),
      /* @__PURE__ */ jsx(Table, { className: "w-full text-[13px]", children: /* @__PURE__ */ jsx(Table.ScrollContainer, { children: /* @__PURE__ */ jsxs(Table.Content, { "aria-label": "Pending payments", children: [
        /* @__PURE__ */ jsx(Table.Header, { columns: columns$1, children: (column) => /* @__PURE__ */ jsx(
          Table.Column,
          {
            id: column.key,
            align: column.align,
            isRowHeader: column.key === "payment",
            children: column.label
          },
          column.key
        ) }),
        /* @__PURE__ */ jsx(Table.Body, { items: rows, children: (row) => /* @__PURE__ */ jsx(Table.Row, { id: row.key, children: columns$1.map((column) => /* @__PURE__ */ jsx(Table.Cell, { align: column.align, children: renderCell(row, column.key) }, `${row.key}-${column.key}`)) }, row.key) })
      ] }) }) })
    ] }) });
  },
  {
    name: "PendingPayments",
    sync: (services, { accountId }) => [
      {
        execute: () => accountId != null && services.payments.getByAccount(Number(accountId)),
        deps: [accountId]
      }
    ],
    monitors: (services) => [services.payments.getByAccount]
  }
);
const ORDER_DETAIL = "sales.order-detail";
const InvoiceDetail = reactive.component(
  ({ services, monitors, orderId, row, customerId, onOpenEntry, onStatus, onChanged }) => {
    const id = Number(orderId);
    const [allocations, setAllocations] = useState(null);
    const { components } = useRuntimeProvider();
    const OrderDetail = components.get(ORDER_DETAIL);
    useSuccessReactor(services.allocations.getByOrder, (list, args) => {
      if (Number(args[0]) === id) setAllocations(list ?? []);
    });
    useSuccessReactor(services.allocations.remove, (_payload, [sourceType, allocationId]) => {
      if (!allocations?.some((a) => a.sourceType === sourceType && a.id === allocationId)) return;
      services.allocations.getByOrder(id);
      onChanged?.();
    });
    useSuccessReactor(services.allocations.applyAvailableFunds, (result, [request]) => {
      if (Number(request?.orderId) !== id) return;
      if ((result?.creditsApplied ?? 0) + (result?.paymentsApplied ?? 0) === 0) return;
      services.allocations.getByOrder(id);
      onChanged?.();
    });
    const [paymentDrawer, PaymentDrawer] = useDrawerController(PaymentCreateDrawer, {
      // Required: withDrawerController defaults isOpen to true.
      isOpen: false,
      // Wide enough for the payment-profile form it can swap in, with its address fields side by
      // side; the same width as the ledger entry drawer.
      size: "2xl",
      headerContent: () => "New Payment",
      onSuccess: (payment) => {
        onChanged?.();
        if (payment?.statusType === PaymentStatusType.Paid) {
          services.allocations.applyAvailableFunds({ orderId: id });
        }
      }
    });
    const applied = allocations == null ? null : sumAmounts(allocations);
    const total = row?.amount ?? null;
    const balance = total != null && applied != null ? remainingAmount(total, applied) : row?.entryBalance ?? null;
    const balanceActions = balance > 0 && // One group rather than two loose buttons, which wrapped and stacked in the narrow strip. The
    // policy wrappers and the confirmation popover add no element, so the Buttons are still the
    // group's DOM children and get its corners; each sets its own size and variant, which the
    // wrappers hide from the group.
    /* @__PURE__ */ jsxs(ButtonGroup, { size: "sm", variant: "secondary", "aria-label": "Settle the balance", children: [
      customerId != null && /* @__PURE__ */ jsx(RequirePolicy, { resource: "Payments", operation: "Create", children: /* @__PURE__ */ jsx(
        ConfirmableButton,
        {
          withConfirm: false,
          size: "sm",
          variant: "secondary",
          iconClass: "ri-money-dollar-circle-line",
          label: "New payment",
          onPress: () => paymentDrawer.open({
            accountId: Number(customerId),
            amount: balance,
            invoiceBalance: balance
          })
        }
      ) }),
      /* @__PURE__ */ jsx(RequirePolicy, { resource: "Payments", operation: "Update", children: /* @__PURE__ */ jsx(
        ConfirmableButton,
        {
          withConfirm: true,
          size: "sm",
          variant: "secondary",
          confirmColor: "primary",
          startContent: customerId != null && /* @__PURE__ */ jsx(ButtonGroup.Separator, {}),
          iconClass: "ri-hand-coin-line",
          label: "Allocate funds",
          isLoading: monitors.applyAvailableFunds.executing,
          body: /* @__PURE__ */ jsxs("div", { children: [
            "Apply the account's unallocated credits and payments to the",
            " ",
            /* @__PURE__ */ jsx("strong", { children: formatMoney(balance) }),
            " still due on this order? Nothing is applied beyond that balance."
          ] }),
          onPress: () => services.allocations.applyAvailableFunds({ orderId: id })
        }
      ) })
    ] });
    return /* @__PURE__ */ jsxs(Fragment$1, { children: [
      /* @__PURE__ */ jsx(
        StatStrip,
        {
          stats: [
            { label: "Total", value: total },
            { label: "Applied", value: applied, isLoading: applied == null },
            {
              label: "Balance due",
              value: balance,
              tone: balance > 0 ? "danger" : "neutral",
              isLoading: balance == null,
              // The ways to settle it sit with the figure, not in a footer away from it.
              actions: balanceActions
            }
          ]
        }
      ),
      /* @__PURE__ */ jsx(DetailSection, { title: "Order", noPad: true, children: OrderDetail ? /* @__PURE__ */ jsx(OrderDetail, { orderId: id, onLoaded: (order) => onStatus?.(order.statusTypeName) }) : /* @__PURE__ */ jsx(
        DetailEmpty,
        {
          icon: "ri-shopping-cart-line",
          title: "Order detail unavailable",
          description: "The Sales app is not loaded in this host."
        }
      ) }),
      customerId != null && /* @__PURE__ */ jsx(RequirePolicy, { resource: "Payments", operation: "ChangeStatus", children: /* @__PURE__ */ jsx(PendingPayments, { accountId: Number(customerId), onOpenEntry }) }),
      /* @__PURE__ */ jsx(DetailSection, { title: "Applied payments & credits", count: allocations?.length, noPad: true, children: /* @__PURE__ */ jsx(
        AllocationsList,
        {
          allocations,
          isLoading: monitors.getByOrder.executing || monitors.remove.executing,
          emptyTitle: "Nothing applied yet",
          emptyCause: "No payment or credit has been allocated to this order.",
          entryOf: (allocation) => ({
            type: allocation.sourceType === AllocationSourceType.Credit ? "Account Credit" : "Payment",
            id: allocation.sourceId,
            label: allocation.sourceDescription
          }),
          onOpenEntry,
          onRemove: (allocation) => services.allocations.remove(allocation.sourceType, allocation.id)
        }
      ) }),
      /* @__PURE__ */ jsx(PaymentDrawer, {})
    ] });
  },
  {
    name: "InvoiceDetail",
    sync: (services, { orderId }) => [
      {
        execute: () => orderId && services.allocations.getByOrder(Number(orderId)),
        deps: [orderId]
      }
    ],
    monitors: (services) => [
      services.allocations.getByOrder,
      services.allocations.remove,
      services.allocations.applyAvailableFunds
    ]
  }
);
const frameKey = (frame) => relatedKey(frame.type, frame.associatedId);
const LedgerEntryPanel = ({
  item,
  onClose,
  onSuccess,
  onChanged,
  onNavigate,
  resolveRow
}) => {
  const user = useUser();
  const config = useConfig();
  const apiKey = config?.api?.apiKey ?? "";
  const canViewAllocations = user.hasPolicy(apiKey, "Payments", "View");
  const [stack, setStack] = useState(() => item ? [item] : []);
  const [statuses, setStatuses] = useState({});
  useEffect(() => {
    setStack(item ? [item] : []);
    setStatuses({});
  }, [item]);
  const current = stack[stack.length - 1];
  if (!current) return null;
  const key = frameKey(current);
  const status = statuses[key] ?? current.row?.status ?? null;
  const setStatus = (name) => setStatuses((s) => s[key] === name ? s : { ...s, [key]: name });
  const openEntry = (type, id) => {
    const frame = {
      type,
      associatedId: id,
      customerId: current.customerId,
      row: resolveRow?.(type, id) ?? null
    };
    setStack((s) => [...s, frame]);
    onNavigate?.(frame);
  };
  const back = () => {
    if (stack.length < 2) return;
    const previous = stack[stack.length - 2];
    setStack((s) => s.slice(0, -1));
    onNavigate?.(previous);
  };
  const common = {
    row: current.row,
    customerId: current.customerId,
    onOpenEntry: openEntry,
    onStatus: setStatus,
    onBack: onClose
  };
  let body;
  if (isPaymentLike(current.type)) {
    body = /* @__PURE__ */ jsx(
      PaymentDetail,
      {
        ...common,
        paymentId: current.associatedId,
        entryType: current.type,
        onChanged,
        onSuccess
      }
    );
  } else if (current.type === "Account Credit") {
    body = /* @__PURE__ */ jsx(
      CreditDetail,
      {
        ...common,
        creditId: current.associatedId,
        canViewAllocations,
        onChanged
      }
    );
  } else if (current.type === "Invoice") {
    body = /* @__PURE__ */ jsx(
      InvoiceDetail,
      {
        ...common,
        orderId: current.associatedId,
        onChanged
      }
    );
  } else {
    body = /* @__PURE__ */ jsx(LedgerEntryFallback, { row: current.row });
  }
  return /* @__PURE__ */ jsxs("div", { className: "flex h-full flex-col", children: [
    /* @__PURE__ */ jsx(
      LedgerEntryHeader,
      {
        title: `${current.type} #${current.associatedId}`,
        status,
        canBack: stack.length > 1,
        onBack: back
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "min-h-0 flex-1 overflow-y-auto", children: body }, key)
  ] });
};
const LedgerEntryDrawer = withDrawerController(LedgerEntryPanel);
const CREDIT_STATUS_OPTIONS = [
  { value: CreditStatusType.Approved, label: "Approved" },
  { value: CreditStatusType.Declined, label: "Declined" },
  { value: CreditStatusType.Pending, label: "Pending" }
];
const filters = new Filters([
  new FilterInput({
    name: "accountName",
    title: "Account Name"
  }),
  new FilterInput({
    name: "accountId",
    title: "Account #",
    options: { searchType: "equals" }
  }),
  new FilterSelect({
    name: "status",
    title: "Status",
    options: { searchType: "equals" },
    render: { inputType: "integer" },
    loader: () => Promise.resolve(CREDIT_STATUS_OPTIONS)
  }),
  new FilterInput({
    name: "availableAmount",
    title: "Available more than",
    options: { searchType: "greaterThan" }
  }),
  new FilterDateRange({
    name: "effectiveOnUtc",
    title: "Effective On"
  }),
  new FilterDateRange({
    name: "createdOnUtc",
    title: "Created On"
  })
]);
const sorting = new Sorting(
  [
    { value: "id", title: "Credit #" },
    { value: "accountName", title: "Account" },
    { value: "status", title: "Status" },
    { value: "amount", title: "Amount" },
    { value: "allocatedAmount", title: "Allocated" },
    { value: "availableAmount", title: "Available" },
    { value: "effectiveOnUtc", title: "Effective On" },
    { value: "createdOnUtc", title: "Created On" }
  ],
  "createdOnUtc",
  "desc"
);
const Money = ({ value, tone, bold }) => /* @__PURE__ */ jsx(
  "span",
  {
    className: `tabular-nums ${bold ? "font-semibold" : "font-medium"} ${tone ?? "text-[var(--foreground)]"}`,
    children: formatCurrencyUSD(Number(value) || 0)
  }
);
const availableTone = (value) => value > 0 ? "text-[var(--pill-success-fg)]" : value < 0 ? "text-[var(--pill-danger-fg)]" : "text-[var(--fg-3)]";
const CreditsTable = ({ credits = [], reasons, onSelect }) => {
  const reasonName = useCallback(
    (reason) => reasons?.find((r) => Number(r.id) === Number(reason))?.name ?? (reasons ? `Reason ${reason}` : "…"),
    [reasons]
  );
  const columns2 = useMemo(
    () => [
      {
        id: "id",
        header: "Credit #",
        isRowHeader: true,
        allowsSorting: true,
        width: 110,
        minWidth: 90,
        cell: (row) => /* @__PURE__ */ jsxs("span", { className: "font-mono font-semibold", children: [
          "#",
          row.id
        ] })
      },
      {
        id: "accountName",
        header: "Account",
        allowsSorting: true,
        allowsResizing: true,
        minWidth: 220,
        cell: (row) => /* @__PURE__ */ jsxs("div", { className: "flex flex-col leading-tight", children: [
          /* @__PURE__ */ jsx("span", { className: "text-[var(--foreground)]", children: row.accountName ?? "—" }),
          /* @__PURE__ */ jsxs("span", { className: "font-mono text-[11.5px] text-[var(--fg-3)]", children: [
            "#",
            row.accountId
          ] })
        ] })
      },
      {
        id: "reason",
        header: "Reason",
        minWidth: 160,
        cell: (row) => /* @__PURE__ */ jsx("span", { className: "text-[var(--muted)]", children: reasonName(row.reason) })
      },
      {
        id: "status",
        header: "Status",
        allowsSorting: true,
        width: 120,
        cell: (row) => {
          const name = creditStatusName(row.status);
          return name ? /* @__PURE__ */ jsx(StatusPill, { status: name }) : /* @__PURE__ */ jsx("span", { children: "—" });
        }
      },
      {
        id: "amount",
        header: "Amount",
        allowsSorting: true,
        align: "end",
        width: 120,
        cell: (row) => /* @__PURE__ */ jsx(Money, { value: row.amount, bold: true })
      },
      {
        id: "allocatedAmount",
        header: "Allocated",
        allowsSorting: true,
        align: "end",
        width: 120,
        cell: (row) => /* @__PURE__ */ jsx(Money, { value: row.allocatedAmount, tone: "text-[var(--muted)]" })
      },
      {
        id: "availableAmount",
        header: "Available",
        allowsSorting: true,
        align: "end",
        width: 120,
        cell: (row) => /* @__PURE__ */ jsx(Money, { value: row.availableAmount, tone: availableTone(row.availableAmount), bold: true })
      },
      {
        id: "effectiveOnUtc",
        header: "Effective",
        allowsSorting: true,
        width: 120,
        cell: (row) => /* @__PURE__ */ jsx("span", { className: "tabular-nums text-[var(--muted)]", children: row.effectiveOnUtc ? parseAndFormatDate(row.effectiveOnUtc) : "—" })
      },
      {
        id: "note",
        header: "Note",
        allowsResizing: true,
        minWidth: 200,
        cell: (row) => /* @__PURE__ */ jsx("span", { className: "block truncate text-[var(--muted)]", title: row.note ?? "", children: row.note ?? "" })
      },
      {
        id: "actions",
        header: "",
        pinned: "end",
        width: 64,
        minWidth: 64,
        align: "center",
        cell: (row) => /* @__PURE__ */ jsx(
          Button,
          {
            isIconOnly: true,
            size: "sm",
            variant: "light",
            onPress: () => onSelect?.(row),
            "aria-label": "View credit",
            className: "text-[var(--fg-3)] hover:text-[var(--foreground)]",
            children: /* @__PURE__ */ jsx("i", { className: "ri-arrow-right-s-line" })
          }
        )
      }
    ],
    [reasonName, onSelect]
  );
  return /* @__PURE__ */ jsx(
    RequestBrokerDataGrid,
    {
      "aria-label": "Account credits",
      columns: columns2,
      data: credits,
      getRowId: (row) => row.id,
      allowsColumnResize: true,
      onRowAction: (key) => {
        const selected = credits.find((item) => String(item.id) === String(key));
        if (selected) onSelect?.(selected);
      }
    }
  );
};
const CreditsListController = reactive.component(
  ({ services, data, monitors }) => {
    const { credits } = data;
    const [reasons, setReasons] = useState(null);
    const [reloadToken, setReloadToken] = useState(0);
    const reload = useCallback(() => setReloadToken((token) => token + 1), []);
    useSuccessReactor(services.credits.getReasons, (list) => setReasons(list ?? []));
    const [drawer, Drawer] = useDrawerController(LedgerEntryDrawer, {
      // Required: withDrawerController defaults isOpen to true.
      isOpen: false,
      size: "2xl",
      onSuccess: reload
    });
    const openCredit = (credit) => drawer.open({
      type: "Account Credit",
      associatedId: credit.id,
      customerId: credit.accountId,
      // No ledger row here; the header pill waits for the detail's `onStatus`.
      row: null
    });
    const handleExecute = useCallback((action, metadata) => {
      action(metadata);
    }, []);
    return /* @__PURE__ */ jsxs(Fragment$1, { children: [
      /* @__PURE__ */ jsx(
        RequestBroker,
        {
          actionKey: "queryCredits",
          action: services.credits.queryCredits,
          filters,
          sorting,
          isExecuting: monitors.queryCredits.executing,
          onExecute: handleExecute,
          deps: [reloadToken],
          children: /* @__PURE__ */ jsx(CreditsTable, { credits, reasons, onSelect: openCredit })
        }
      ),
      /* @__PURE__ */ jsx(Drawer, { classNames: { body: "p-0" }, onChanged: reload })
    ] });
  },
  {
    name: "CreditsListController",
    sync: (services) => [{ execute: () => services.credits.getReasons(), deps: [] }],
    queries: (_, services) => [
      {
        name: "credits",
        execute: (db2) => db2.withRequestBroker(services.credits.queryCredits.action, () => db2.credits, {
          sorting: true
        }),
        deps: []
      }
    ],
    monitors: (services) => [services.credits.queryCredits, services.credits.getReasons]
  }
);
const CreditsView = () => /* @__PURE__ */ jsxs("div", { className: "flex h-full min-h-0 flex-col", children: [
  /* @__PURE__ */ jsx("div", { className: "mb-4 flex flex-row justify-between", children: /* @__PURE__ */ jsxs(Breadcrumbs, { className: "content-center", children: [
    /* @__PURE__ */ jsx(BreadcrumbsItem, { isDisabled: true, children: "Billing" }),
    /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Credits" })
  ] }) }),
  /* @__PURE__ */ jsx("div", { className: "flex min-h-0 flex-1 flex-col", children: /* @__PURE__ */ jsx(CreditsListController, {}) })
] });
const CreditsRoutes = () => /* @__PURE__ */ jsx(Routes, { children: /* @__PURE__ */ jsx(Route, { path: "/*", element: /* @__PURE__ */ jsx(CreditsView, {}) }) });
const Router = () => /* @__PURE__ */ jsx(AppLayout, { children: /* @__PURE__ */ jsxs(Routes, { children: [
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "credits/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "credits", operation: "query", fallback: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(CreditsRoutes, {}) })
    }
  ),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "credits", operation: "query", fallback: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(CreditsRoutes, {}) })
    }
  )
] }) });
function getBillingNavigation(runtime) {
  const user = runtime.user;
  const apiKey = runtime.config?.api?.apiKey ?? "";
  return {
    isVisible: user.hasApiAccess(apiKey),
    items: [
      {
        title: "Review Credits",
        route: "/credits",
        icon: "ri-coupon-3-line",
        isVisible: user.hasPolicy(apiKey, "credits", "query")
      }
    ]
  };
}
const RoutePaths = () => {
  const baseUrl = appRuntime.baseUrl;
  const rootPaths = {
    payments: baseUrl + "/payments",
    credits: baseUrl + "/credits"
  };
  return {
    payments: {
      list: () => rootPaths.payments,
      create: () => `${rootPaths.payments}/create`,
      details: (id) => `${rootPaths.payments}/${id}/details`
    },
    credits: {
      list: () => rootPaths.credits
    }
  };
};
function getBillingResources(runtime) {
  const user = runtime.user;
  const apiKey = runtime.config?.api?.apiKey ?? "";
  const items = [];
  if (user.hasPolicy(apiKey, "payments", "create")) {
    items.push({
      key: "payment",
      label: "New Payment",
      route: "/payments",
      description: "Create new payment"
    });
  }
  return {
    items,
    handlers: ((navigate) => ({
      payment: () => navigate(RoutePaths().payments.create())
    }))
  };
}
const Embed = createEmbed({ runtime: () => appRuntime, render });
const OrderCreatePanel = ({ item, onSuccess }) => {
  const OrderCreate = useComponent("sales.order-create");
  return /* @__PURE__ */ jsx(OrderCreate, { accountId: item?.accountId, onSuccess });
};
const OrderCreateDrawer = withDrawerController(OrderCreatePanel);
const LedgerSummaryActions = {
  Reload: "reload",
  /** Apply the account's unallocated payments and credits to its unpaid orders, keeping what is already applied. */
  AllocateFunds: "allocate-funds",
  /** Re-run allocation across the account from scratch, then reload. */
  Rebuild: "rebuild",
  /** Grant the account a new credit. */
  CreateCredit: "create-credit",
  /** Place a new order for the account. */
  CreateOrder: "create-order",
  /** Record a payment received, or charge a stored profile. */
  RecordPayment: "record-payment",
  /** Add the credits an outside collection effort produced, together. */
  AddCollectionsCredits: "add-collections-credits"
};
const menuItems = [
  {
    key: LedgerSummaryActions.Reload,
    title: "Reload",
    icon: "ri-refresh-line",
    resource: "ledgers",
    operation: "view",
    apiKey: "BillingAPI"
  },
  {
    key: LedgerSummaryActions.AllocateFunds,
    title: "Allocate Funds",
    icon: "ri-funds-line",
    resource: "allocations",
    operation: "apply",
    apiKey: "BillingAPI"
  },
  {
    key: LedgerSummaryActions.Rebuild,
    title: "Rebuild",
    icon: "ri-tools-line",
    resource: "allocations",
    operation: "apply",
    apiKey: "BillingAPI"
  },
  {
    key: LedgerSummaryActions.CreateCredit,
    title: "New Credit",
    icon: "ri-coupon-line",
    resource: "credits",
    operation: "create",
    apiKey: "BillingAPI"
  },
  {
    key: LedgerSummaryActions.CreateOrder,
    title: "New Order",
    icon: "ri-shopping-cart-line",
    resource: "orders",
    operation: "create",
    apiKey: "SalesAPI"
  },
  {
    key: LedgerSummaryActions.RecordPayment,
    title: "New Payment",
    icon: "ri-money-dollar-circle-line",
    resource: "payments",
    operation: "create",
    apiKey: "BillingAPI"
  },
  {
    key: LedgerSummaryActions.AddCollectionsCredits,
    title: "Add Collections Credits",
    icon: "ri-hand-coin-line",
    resource: "credits",
    operation: "create",
    apiKey: "BillingAPI"
  }
];
const toneClass = { danger: "text-danger", success: "text-success" };
const Stat = ({ label, value, style, tone, isLoading }) => /* @__PURE__ */ jsxs(KPI, { children: [
  /* @__PURE__ */ jsx(KPI.Header, { children: /* @__PURE__ */ jsx(KPI.Title, { children: label }) }),
  /* @__PURE__ */ jsx(KPI.Content, { children: isLoading ? /* @__PURE__ */ jsx(Skeleton, { className: "h-7 w-24 rounded-md", "aria-label": "Loading" }) : /* @__PURE__ */ jsx(
    KPI.Value,
    {
      value: value ?? 0,
      style,
      currency: style === "currency" ? "USD" : void 0,
      maximumFractionDigits: style === "currency" ? 2 : 0,
      className: toneClass[tone]
    }
  ) })
] });
const LedgerSummary$1 = ({
  summary,
  isLoading,
  showActions = true,
  frameless = false,
  onAction,
  onFullScreen
}) => {
  const items = useMemo(() => menuItems, []);
  const { user } = useRuntimeProvider();
  const disabledKeys = menuItems.filter((x) => !user.hasPolicy(x.apiKey, x.resource, x.operation)).map((x) => x.key);
  const stats = [
    {
      label: "Total Unpaid Orders",
      value: summary?.totalUnpaidAmount,
      style: "currency",
      tone: summary?.totalUnpaidAmount > 0 ? "danger" : void 0
    },
    {
      label: "Orders With Balance",
      value: summary?.totalUnpaidCount,
      style: "decimal",
      tone: summary?.totalUnpaidCount > 0 ? "danger" : void 0
    },
    {
      label: "Unallocated Payments",
      value: summary?.unallocatedPaymentAmount,
      style: "currency",
      tone: summary?.unallocatedPaymentAmount > 0 ? "success" : void 0
    },
    {
      label: "Unallocated Credits",
      value: summary?.unallocatedCreditAmount,
      style: "currency",
      tone: summary?.unallocatedCreditAmount > 0 ? "success" : void 0
    }
  ];
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: `flex w-full flex-col items-stretch overflow-hidden md:flex-row ${frameless ? "" : "rounded-2xl border border-[var(--border)] bg-[var(--surface)]"}`,
      "aria-busy": isLoading || void 0,
      children: [
        /* @__PURE__ */ jsx(KPIGroup, { className: "flex-1 flex-col rounded-none md:flex-row", children: stats.map((stat, index) => /* @__PURE__ */ jsxs(Fragment, { children: [
          index > 0 && /* @__PURE__ */ jsx(KPIGroup.Separator, { className: "my-0 h-px w-auto md:my-4 md:h-auto md:w-px" }),
          /* @__PURE__ */ jsx(Stat, { ...stat, isLoading })
        ] }, stat.label)) }),
        showActions && /* @__PURE__ */ jsx("div", { className: "flex flex-none items-center justify-end border-t border-[var(--border)] px-4 py-2 md:justify-center md:border-t-0 md:border-l md:py-0 gap-2", children: /* @__PURE__ */ jsxs(ButtonGroup, { size: "sm", variant: "primary", "aria-label": "Ledger toolbar", children: [
          /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
            /* @__PURE__ */ jsx(
              Button,
              {
                isIconOnly: true,
                size: "sm",
                variant: "primary",
                "aria-label": "Reload ledger",
                onPress: () => onAction?.(LedgerSummaryActions.Reload),
                children: /* @__PURE__ */ jsx("i", { className: "ri-refresh-line" })
              }
            ),
            /* @__PURE__ */ jsx(Tooltip.Content, { children: "Reload" })
          ] }),
          onFullScreen && /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
            /* @__PURE__ */ jsxs(
              Button,
              {
                isIconOnly: true,
                size: "sm",
                variant: "primary",
                "aria-label": "Full screen",
                onPress: onFullScreen,
                children: [
                  /* @__PURE__ */ jsx(ButtonGroup.Separator, {}),
                  /* @__PURE__ */ jsx("i", { className: "ri-fullscreen-line" })
                ]
              }
            ),
            /* @__PURE__ */ jsx(Tooltip.Content, { children: "Full screen" })
          ] }),
          /* @__PURE__ */ jsxs(Dropdown, { isDisabled: isLoading, children: [
            /* @__PURE__ */ jsxs(Button, { isIconOnly: true, size: "sm", variant: "primary", "aria-label": "Ledger actions", children: [
              /* @__PURE__ */ jsx(ButtonGroup.Separator, {}),
              /* @__PURE__ */ jsx("i", { className: "ri-more-2-line" })
            ] }),
            /* @__PURE__ */ jsx(Dropdown.Popover, { children: /* @__PURE__ */ jsx(
              Dropdown.Menu,
              {
                items,
                disabledKeys,
                onAction: (key) => onAction?.(key),
                children: (item) => /* @__PURE__ */ jsxs(Dropdown.Item, { id: item.key, textValue: item.title, children: [
                  /* @__PURE__ */ jsx("i", { className: `${item.icon} mr-2` }),
                  /* @__PURE__ */ jsx(Label, { children: item.title })
                ] }, item.key)
              }
            ) })
          ] })
        ] }) })
      ]
    }
  );
};
const LedgerSummaryController = reactive.component(
  ({ services, monitors, customerId, showActions, frameless, onAction, onFullScreen }) => {
    const [summary, setSummary] = useState(null);
    const [creditDrawer, CreditDrawer] = useDrawerController(CreditCreateDrawer, {
      size: "md",
      // Required: withDrawerController defaults isOpen to true.
      isOpen: false,
      headerContent: () => "New Credit",
      onSuccess: () => handleSummaryAction(LedgerSummaryActions.Reload)
    });
    const [orderDrawer, OrderDrawer] = useDrawerController(OrderCreateDrawer, {
      size: "2xl",
      isOpen: false,
      headerContent: () => "New Order",
      onSuccess: () => handleSummaryAction(LedgerSummaryActions.Reload)
    });
    const [paymentDrawer, PaymentDrawer] = useDrawerController(PaymentCreateDrawer, {
      // Wide enough for the payment-profile form it can swap in, with its address fields side by
      // side; the same width as the ledger entry drawer.
      size: "2xl",
      isOpen: false,
      headerContent: () => "New Payment",
      onSuccess: () => handleSummaryAction(LedgerSummaryActions.Reload)
    });
    const [collectionsDrawer, CollectionsDrawer] = useDrawerController(
      CollectionsCreditsCreateDrawer,
      {
        size: "md",
        isOpen: false,
        headerContent: () => "Add Collections Credits",
        onSuccess: () => handleSummaryAction(LedgerSummaryActions.Reload)
      }
    );
    useSuccessReactor(services.ledgers.getSummary, (payload, args) => {
      if (Number(args[0]) === Number(customerId)) setSummary(payload);
    });
    const handleSummaryAction = (key) => {
      switch (key) {
        case LedgerSummaryActions.Reload:
          services.ledgers.getSummary(Number(customerId));
          onAction?.(key);
          break;
        case LedgerSummaryActions.AllocateFunds:
          services.allocations.reallocate({
            accountId: Number(customerId),
            removeExistingAllocations: false
          });
          onAction?.(key);
          break;
        case LedgerSummaryActions.Rebuild:
          services.allocations.reallocate({
            accountId: Number(customerId),
            removeExistingAllocations: true
          });
          onAction?.(key);
          break;
        case LedgerSummaryActions.CreateCredit:
          creditDrawer.open({ accountId: Number(customerId) });
          onAction?.(key);
          break;
        case LedgerSummaryActions.CreateOrder:
          orderDrawer.open({ accountId: Number(customerId) });
          onAction?.(key);
          break;
        case LedgerSummaryActions.RecordPayment:
          paymentDrawer.open({
            accountId: Number(customerId),
            availableCredit: summary?.unallocatedCreditAmount ?? 0
          });
          onAction?.(key);
          break;
        case LedgerSummaryActions.AddCollectionsCredits:
          collectionsDrawer.open({ accountId: Number(customerId) });
          onAction?.(key);
          break;
        default:
          onAction?.(key);
      }
    };
    useSuccessReactor(services.allocations.reallocate, (_payload, args) => {
      if (Number(args[0]?.accountId) === Number(customerId)) {
        handleSummaryAction(LedgerSummaryActions.Reload);
      }
    });
    return /* @__PURE__ */ jsxs(Fragment$1, { children: [
      /* @__PURE__ */ jsx(
        LedgerSummary$1,
        {
          summary,
          isLoading: monitors.getSummary.executing || monitors.reallocate.executing,
          showActions,
          frameless,
          onAction: handleSummaryAction,
          onFullScreen
        }
      ),
      /* @__PURE__ */ jsx(CreditDrawer, {}),
      /* @__PURE__ */ jsx(OrderDrawer, {}),
      /* @__PURE__ */ jsx(PaymentDrawer, {}),
      /* @__PURE__ */ jsx(CollectionsDrawer, {})
    ] });
  },
  {
    name: "LedgerSummaryController",
    sync: (services, { customerId }) => [
      {
        execute: () => customerId && services.ledgers.getSummary(Number(customerId)),
        deps: [customerId]
      }
    ],
    monitors: (services) => [services.ledgers.getSummary, services.allocations.reallocate]
  }
);
const LEDGER_SUMMARY = "billing.ledger-summary";
const LedgerSummary = (props) => createElement(
  Embed,
  null,
  // The controller is JS, so its props are inferred rather than declared. The interface above is
  // the contract; this cast is what ties the two together.
  createElement(LedgerSummaryController, props)
);
const LedgerSummaryWidget$1 = ({ customerId, showActions = false, onAction }) => /* @__PURE__ */ jsxs(Widget, { className: "w-full", children: [
  /* @__PURE__ */ jsx(Widget.Header, { children: /* @__PURE__ */ jsx(Widget.Title, { children: "Ledger" }) }),
  /* @__PURE__ */ jsx(Widget.Content, { className: "p-0", children: /* @__PURE__ */ jsx(
    LedgerSummaryController,
    {
      customerId,
      showActions,
      frameless: true,
      onAction
    }
  ) })
] });
const LEDGER_SUMMARY_WIDGET = "billing.ledger-summary-widget";
const LedgerSummaryWidget = (props) => createElement(
  Embed,
  null,
  createElement(LedgerSummaryWidget$1, props)
);
const PaymentPlanMarker = ({ row }) => {
  const label = paymentPlanLabel(row);
  if (!label) return null;
  return /* @__PURE__ */ jsx(
    "span",
    {
      role: "img",
      "aria-label": label,
      title: label,
      className: "ml-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-accent/15 align-middle text-[10px] font-bold leading-none text-accent",
      children: "P"
    }
  );
};
const columns = [
  { key: "date", label: "Date" },
  { key: "type", label: "Type" },
  { key: "subType", label: "Category" },
  { key: "associatedId", label: "ID" },
  { key: "status", label: "Status" },
  { key: "entryBalance", label: "Item Balance", align: "end" },
  { key: "amount", label: "Amount", align: "end" },
  { key: "effectiveAmount", label: "Effective Amount", align: "end" },
  { key: "runningBalance", label: "Balance", align: "end" },
  { key: "related", label: "", align: "center" }
];
const LedgerEntriesTable = ({
  entries,
  related,
  detailKey,
  onOpenEntry,
  onToggleRelated,
  onClearRelated,
  isLoading
}) => {
  const isRelated = (row) => related?.matches.has(relatedKey(row.type, row.associatedId));
  const renderCell = (row, columnKey) => {
    switch (columnKey) {
      case "date":
        return /* @__PURE__ */ jsxs(Fragment$1, { children: [
          /* @__PURE__ */ jsx("span", { className: "tabular-nums text-[var(--muted)]", children: parseAndFormatDate(row.entryDateUtc) }),
          " ",
          /* @__PURE__ */ jsx("span", { className: "text-[var(--fg-3)] text-[11.5px]", children: parseAndFormatTime(row.entryDateUtc) })
        ] });
      case "type":
        return /* @__PURE__ */ jsxs(Fragment$1, { children: [
          /* @__PURE__ */ jsx("span", { className: "text-[var(--foreground)]", children: row.type }),
          /* @__PURE__ */ jsx(PaymentPlanMarker, { row })
        ] });
      case "subType":
        return /* @__PURE__ */ jsx("span", { className: "text-[var(--muted)]", children: row.subType });
      case "associatedId":
        return /* @__PURE__ */ jsxs(
          Button,
          {
            variant: "light",
            color: "primary",
            onPress: () => onOpenEntry?.(row),
            className: "h-auto min-w-0 p-0 font-mono font-semibold",
            children: [
              "#",
              row.associatedId
            ]
          }
        );
      case "status":
        return /* @__PURE__ */ jsx(StatusPill, { status: row.status });
      case "entryBalance":
        return /* @__PURE__ */ jsx(AmountCell, { n: row.entryBalance });
      case "amount":
        return /* @__PURE__ */ jsx(AmountCell, { n: row.amount, bold: row.type === "Invoice" });
      case "effectiveAmount":
        return /* @__PURE__ */ jsx(AmountCell, { n: row.effectiveAmount });
      case "runningBalance":
        return /* @__PURE__ */ jsx(AmountCell, { n: row.runningBalance, bold: true });
      case "related": {
        const active = related?.key === relatedKey(row.type, row.associatedId);
        return /* @__PURE__ */ jsx(
          Button,
          {
            isIconOnly: true,
            size: "sm",
            variant: "bordered",
            color: active ? "warning" : "default",
            onPress: () => onToggleRelated(row),
            title: active ? "Clear related records" : "Highlight related records",
            className: `h-6 w-6 min-w-6 ${active ? "" : "border-[var(--border)] text-[var(--fg-3)]"}`,
            children: /* @__PURE__ */ jsx("i", { className: "ri-links-line text-[13px]" })
          }
        );
      }
      default:
        return null;
    }
  };
  return /* @__PURE__ */ jsx(Fragment$1, { children: /* @__PURE__ */ jsxs(Card, { className: "relative flex h-full min-h-0 flex-col overflow-hidden border border-[var(--border)] bg-[var(--surface)]", children: [
    /* @__PURE__ */ jsx(LoadingBar, { isLoading, label: "Loading ledger" }),
    /* @__PURE__ */ jsx(
      CardContent,
      {
        className: "min-h-0 flex-1 overflow-auto p-0",
        "aria-busy": isLoading || void 0,
        children: entries.length === 0 ? /* @__PURE__ */ jsx("div", { className: "flex h-full items-center justify-center px-5 py-12 text-center text-[13px] text-[var(--fg-3)]", children: isLoading ? "Loading ledger…" : "No ledger entries." }) : /* @__PURE__ */ jsx(Table, { className: "w-full text-[13px]", children: /* @__PURE__ */ jsx(Table.ScrollContainer, { children: /* @__PURE__ */ jsxs(Table.Content, { "aria-label": "Ledger entries", children: [
          /* @__PURE__ */ jsx(Table.Header, { columns, children: (column) => /* @__PURE__ */ jsx(
            Table.Column,
            {
              id: column.key,
              align: column.align,
              isRowHeader: column.key === "associatedId",
              children: column.label
            },
            column.key
          ) }),
          /* @__PURE__ */ jsx(Table.Body, { items: entries, dependencies: [related, detailKey], children: (item) => {
            const isDetail = detailKey === relatedKey(item.type, item.associatedId);
            const cellBg = isDetail ? "bg-accent/15!" : isRelated(item) ? "bg-warning/15!" : "";
            return /* @__PURE__ */ jsx(Table.Row, { id: item.id, children: columns.map((column) => /* @__PURE__ */ jsx(
              Table.Cell,
              {
                align: column.align,
                className: cellBg,
                children: renderCell(item, column.key)
              },
              `${item.id}-${column.key}`
            )) }, item.id);
          } })
        ] }) }) })
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-none flex-wrap items-center justify-between gap-2 border-t border-[var(--border)] bg-[var(--surface)] px-4 py-2.5 text-[12.5px] text-[var(--fg-3)]", children: [
      /* @__PURE__ */ jsxs("span", { children: [
        entries.length,
        " entries",
        related && /* @__PURE__ */ jsxs("span", { className: "ml-3 font-medium text-warning", children: [
          /* @__PURE__ */ jsx("i", { className: "ri-links-line mr-1 align-middle" }),
          entries.filter(isRelated).length,
          " related records highlighted",
          /* @__PURE__ */ jsx(
            Button,
            {
              variant: "light",
              size: "sm",
              color: "warning",
              onPress: onClearRelated,
              className: "h-auto min-w-0 px-1 py-0 ml-2",
              children: "Clear"
            }
          )
        ] })
      ] }),
      /* @__PURE__ */ jsx("span", { children: "Click an ID to view details · Click the link icon to highlight related records" })
    ] })
  ] }) });
};
const withRowIds = (entries) => (entries ?? []).map((entry, index) => ({ ...entry, id: index + 1 }));
const CustomerLedgerController = reactive.component(
  ({ services, monitors, customerId, onSummaryAction }) => {
    const [entries, setEntries] = useState([]);
    const [related, setRelated] = useState(null);
    const [detailKey, setDetailKey] = useState(null);
    const fullScreen = useOverlayState();
    const reload = () => {
      services.ledgers.getByCustomer(Number(customerId));
      services.ledgers.getSummary(Number(customerId));
    };
    const [entryDrawer, EntryDrawer] = useDrawerController(LedgerEntryDrawer, {
      // Required: withDrawerController defaults isOpen to true.
      isOpen: false,
      size: "2xl",
      onClose: () => setDetailKey(null),
      onSuccess: () => {
        setDetailKey(null);
        reload();
      }
    });
    useSuccessReactor(services.ledgers.getByCustomer, (payload, args) => {
      if (Number(args[0]) !== Number(customerId)) return;
      setEntries(withRowIds(payload));
      setRelated(null);
    });
    useSuccessReactor(services.ledgers.getRelatedEntries, (payload, args) => {
      setRelated({
        key: relatedKey(args[0], args[1]),
        matches: new Set((payload ?? []).map((r) => relatedKey(r.type, r.id)))
      });
    });
    const onToggleRelated = (row) => {
      const key = relatedKey(row.type, row.associatedId);
      if (related?.key === key) return setRelated(null);
      services.ledgers.getRelatedEntries(row.type, row.associatedId);
    };
    const openEntry = (row) => {
      setDetailKey(relatedKey(row.type, row.associatedId));
      entryDrawer.open({
        type: row.type,
        associatedId: row.associatedId,
        customerId: Number(customerId),
        row
      });
    };
    const resolveRow = (type, id) => entries.find((e) => e.type === type && Number(e.associatedId) === Number(id)) ?? null;
    const handleSummaryAction = (key) => {
      switch (key) {
        case LedgerSummaryActions.Reload:
          services.ledgers.getByCustomer(Number(customerId));
          onSummaryAction?.(key);
          break;
        default:
          onSummaryAction?.(key);
      }
    };
    const tableProps = {
      entries,
      related,
      detailKey,
      onOpenEntry: openEntry,
      onToggleRelated,
      onClearRelated: () => setRelated(null),
      isLoading: monitors.getByCustomer.executing || monitors.reallocate.executing
    };
    const detailDrawer = /* @__PURE__ */ jsx(
      EntryDrawer,
      {
        classNames: { body: "p-0" },
        resolveRow,
        onChanged: reload,
        onNavigate: (frame) => setDetailKey(relatedKey(frame.type, frame.associatedId))
      }
    );
    return /* @__PURE__ */ jsxs(FlatCard, { children: [
      /* @__PURE__ */ jsx(FlatCard.Header, { children: /* @__PURE__ */ jsx(
        LedgerSummaryController,
        {
          customerId,
          onAction: handleSummaryAction,
          onFullScreen: fullScreen.open
        }
      ) }),
      /* @__PURE__ */ jsx(FlatCard.Main, { className: "flex flex-col", children: /* @__PURE__ */ jsx(LedgerEntriesTable, { ...tableProps }) }),
      !fullScreen.isOpen && detailDrawer,
      /* @__PURE__ */ jsx(Modal.Backdrop, { isOpen: fullScreen.isOpen, onOpenChange: fullScreen.setOpen, children: /* @__PURE__ */ jsx(Modal.Container, { size: "full", scroll: "inside", children: /* @__PURE__ */ jsxs(Modal.Dialog, { children: [
        /* @__PURE__ */ jsx(Modal.CloseTrigger, {}),
        /* @__PURE__ */ jsx(Modal.Header, { children: /* @__PURE__ */ jsx(Modal.Heading, { children: "Ledger" }) }),
        /* @__PURE__ */ jsx(Modal.Body, { className: "flex flex-col", children: /* @__PURE__ */ jsx(LedgerEntriesTable, { ...tableProps }) }),
        detailDrawer
      ] }) }) })
    ] });
  },
  {
    name: "CustomerLedgerController",
    sync: (services, { customerId }) => [
      {
        execute: () => customerId && services.ledgers.getByCustomer(Number(customerId)),
        deps: [customerId]
      }
    ],
    monitors: (services) => [
      services.ledgers.getByCustomer,
      services.ledgers.getRelatedEntries,
      services.allocations.reallocate
    ]
  }
);
const CUSTOMER_LEDGER = "billing.customer-ledger";
const CustomerLedger = (props) => createElement(
  Embed,
  null,
  // The controller is JS, so its props are inferred rather than declared. The interface above is
  // the contract; this cast is what ties the two together.
  createElement(CustomerLedgerController, props)
);
const PaymentDetailDrawer$1 = ({
  paymentId,
  customerId,
  entryType = "Payment",
  isOpen,
  onClose,
  onChanged
}) => {
  const item = useMemo(
    () => paymentId ? { type: entryType, associatedId: Number(paymentId), customerId: Number(customerId), row: null } : null,
    [paymentId, customerId, entryType]
  );
  return /* @__PURE__ */ jsx(
    LedgerEntryDrawer,
    {
      isOpen: Boolean(isOpen && item),
      item,
      size: "2xl",
      classNames: { body: "p-0" },
      onClose: () => onClose?.(),
      onSuccess: () => {
        onChanged?.();
        onClose?.();
      },
      onChanged: () => onChanged?.()
    }
  );
};
const PAYMENT_DETAIL_DRAWER = "billing.payment-detail-drawer";
const PaymentDetailDrawer = (props) => createElement(
  Embed,
  null,
  // The drawer is JS, so its props are inferred rather than declared. The interface above is the
  // contract; this cast is what ties the two together.
  createElement(PaymentDetailDrawer$1, props)
);
let appRuntime = null;
const initBillingRuntime = (hostRuntime) => {
  appRuntime = hostRuntime.registerApp("billing", {
    description: {
      title: "Billing",
      iconClass: "ri-bank-card-fill",
      defaultRoute: "/payments",
      isVisible: (runtime) => runtime.user.hasApiAccess(runtime.config?.api?.apiKey ?? "")
    },
    routes: Router,
    navigation: getBillingNavigation,
    resources: getBillingResources,
    config: () => BillingConfig.current
  });
  hostRuntime.components.register(LEDGER_SUMMARY, LedgerSummary);
  hostRuntime.components.register(LEDGER_SUMMARY_WIDGET, LedgerSummaryWidget, {
    slot: "accounts.customer-overview",
    order: 20
  });
  hostRuntime.components.register(CUSTOMER_LEDGER, CustomerLedger);
  hostRuntime.components.register(PAYMENT_DETAIL_DRAWER, PaymentDetailDrawer);
};
export {
  initBillingRuntime
};
//# sourceMappingURL=index.js.map
